<template>
  <div class="sidebar-menu">
    <div class="sidebar-icons" ref="sidebarIcons">
      <!-- Text category first -->
      <div class="icon-item" :class="{ active: activeIcon === 'Текст' }" @click="setActiveIcon('Текст')">
        <div class="icon-wrapper">
          <img :src="textIcon" :alt="$t('legacy.Текст')" />
        </div>
        <span class="category-label" ref="categoryLabelText">{{ $t('legacy.Текст') }}</span>
      </div>
      <!-- Other categories from DB -->
      <div class="icon-item" v-for="category in categories" :key="category.id" :class="{ active: activeIcon === category.name }" @click="setActiveIcon(category.name)">
        <div class="icon-wrapper">
          <img :src="category.image" :alt="category.name" />
        </div>
        <span class="category-label" :ref="el => setCategoryLabelRef(el, category.id)">{{ category.name }}</span>
      </div>
    </div>

    <div class="sidebar-content" v-if="showContent">
      <!-- Панель калькулятора (десктоп) -->
      <template v-if="showCalculatorPanel">
        <CalculatorPanel
          :items="calculatorItems"
          :can-see-prices="canSeePrices"
          @close="$emit('close-calculator')"
          @highlight-item="$emit('calculator-highlight', $event)"
          @delete-item="$emit('calculator-delete', $event)"
          @update-excluded="$emit('calculator-excluded', $event)"
          @refresh-prices="$emit('calculator-refresh-prices')"
        />
      </template>
      <!-- Панель слоёв -->
      <template v-else-if="showLayersPanel">
        <div class="layers-sidebar-header">
          <span class="layers-sidebar-title">{{ $t('legacy.Слои') }}</span>
          <span v-if="canSeePrices" class="layers-sidebar-zone-total">{{ zoneTotalPrice }} ₽</span>
        </div>
        <div v-if="canSeePrices" class="layers-sidebar-project-total">
          <span class="project-total-label">{{ $t('legacy.Стоимость проекта:') }}</span>
          <span class="project-total-value">{{ totalProjectPrice }} ₽</span>
        </div>
        <div class="layers-sidebar-list">
          <div v-if="displayLayersList.length === 0" class="layers-sidebar-empty">
            {{ $t('legacy.Нет элементов') }}
          </div>
          <template v-for="(item, index) in displayLayersList" :key="item.id">
            <!-- Group item -->
            <div 
              v-if="item.isGroup"
              class="layers-sidebar-group"
              :class="{ 
                'is-active': activeGroup === item.id, 
                'is-expanded': item.expanded,
                'is-dragging': dragLayerIndex === index,
                'drag-over': dragOverLayerIndex === index && dragLayerIndex !== index
              }"
              draggable="true"
              @dragstart="handleLayerDragStart($event, index)"
              @dragend="handleLayerDragEnd"
              @dragover.prevent="handleLayerDragOver($event, index)"
              @dragleave="handleLayerDragLeave"
              @drop.prevent="handleLayerDrop(index)"
            >
              <div class="layers-sidebar-group-header" @click="$emit('toggle-group-expand', item.id)">
                <svg class="group-expand-icon" :class="{ rotated: item.expanded }" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                  <path d="M2 3L5 6L8 3" stroke="currentColor" stroke-width="1.5" fill="none"/>
                </svg>
                <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                </svg>
                <span class="layers-sidebar-name">{{ item.name }}</span>
                <svg v-if="item.locked" class="lock-icon-small" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <div v-if="item.expanded" class="layers-sidebar-group-children">
                <div 
                  v-for="(child, childIndex) in item.elements" 
                  :key="child.id"
                  class="layers-sidebar-item nested"
                  :class="{ 
                    'is-active': activeItemId === child.id,
                    'is-dragging': dragGroupChildIndex === childIndex && dragGroupId === item.id,
                    'drag-over': dragOverGroupChildIndex === childIndex && dragGroupChildIndex !== childIndex && dragGroupId === item.id
                  }"
                  draggable="true"
                  @dragstart="handleGroupChildDragStart($event, item.id, childIndex)"
                  @dragend="handleGroupChildDragEnd"
                  @dragover.prevent="handleGroupChildDragOver($event, item.id, childIndex)"
                  @dragleave="handleGroupChildDragLeave"
                  @drop.prevent="handleGroupChildDrop(item.id, childIndex)"
                  @click="$emit('select-layer', child.id)"
                >
                  <div class="layers-sidebar-icon">
                    <img v-if="child.type === 'text' || child.isText" src="@/assets/images/icons/text-icon.svg" alt="text" class="layers-type-icon" />
                    <img v-else-if="child.src" :src="child.src" alt="" class="layers-thumb" />
                    <div v-else class="layers-placeholder"></div>
                  </div>
                  <span class="layers-sidebar-name">{{ getLayerName(child) }}</span>
                  <span v-if="canSeePrices && child.effectivePrice > 0" class="layers-sidebar-price">{{ child.effectivePrice }} ₽</span>
                </div>
              </div>
            </div>
            <!-- Regular item -->
            <div 
              v-else
              class="layers-sidebar-item"
              :class="{ 
                'is-active': activeItemId === item.id,
                'is-dragging': dragLayerIndex === index,
                'drag-over': dragOverLayerIndex === index && dragLayerIndex !== index
              }"
              draggable="true"
              @dragstart="handleLayerDragStart($event, index)"
              @dragend="handleLayerDragEnd"
              @dragover.prevent="handleLayerDragOver($event, index)"
              @dragleave="handleLayerDragLeave"
              @drop.prevent="handleLayerDrop(index)"
              @click="$emit('select-layer', item.id)"
            >
              <div class="layers-drag-handle">
                <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor">
                  <circle cx="2" cy="2" r="1.5"/>
                  <circle cx="6" cy="2" r="1.5"/>
                  <circle cx="2" cy="7" r="1.5"/>
                  <circle cx="6" cy="7" r="1.5"/>
                  <circle cx="2" cy="12" r="1.5"/>
                  <circle cx="6" cy="12" r="1.5"/>
                </svg>
              </div>
              <div class="layers-sidebar-icon">
                <img v-if="item.type === 'text' || item.isText" src="@/assets/images/icons/text-icon.svg" alt="text" class="layers-type-icon" />
                <img v-else-if="item.src" :src="item.src" alt="" class="layers-thumb" />
                <div v-else class="layers-placeholder"></div>
              </div>
              <span class="layers-sidebar-name">{{ getLayerName(item) }}</span>
              <span v-if="canSeePrices && item.effectivePrice > 0" class="layers-sidebar-price">{{ item.effectivePrice }} ₽</span>
              <span v-else-if="!canSeePrices && item.effectivePrice > 0" class="layers-sidebar-price locked" :title="$t('legacy.Доступно для премиум подписки')">
                <svg class="lock-icon-small" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </span>
            </div>
          </template>
        </div>
      </template>

      <!-- Обычный контент сайдбара -->
      <template v-else>
        <div class="search-bar">
          <input type="text" :placeholder="$t('legacy.Поиск элемента')" class="search-input" v-model="searchQuery" />
          <img :src="glassIcon" alt="Search Icon" class="search-icon" />
        </div>

        <button 
          class="upload-user-element-btn upload-btn-full-width" 
          :class="{ 'locked': !canAddElements }"
          @click="handleUploadClick"
          :title="!canAddElements ? $t('subscription.customAssetsRequired') : ''"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ $t('legacy.Загрузите свой элемент') }}
          <svg v-if="!canAddElements" class="lock-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
            <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <div class="categories">
        <!-- Секция "Мои цвета" -->
        <div class="category my-colors-section" v-if="showMyColorsSection">
          <div class="category-header" @click="toggleMyColors">
            <div>
              <span>{{ $t('legacy.Мои цвета') }}</span>
              <span>({{ savedColors.length }})</span>
            </div>
            <span class="collapse-row-icon" :class="{ rotated: myColorsOpen }">
              <img :src="collapseRowIcon" alt="Collapse Row Icon" />
            </span>
          </div>
          <transition name="category-transition">
            <div class="category-content" v-if="myColorsOpen">
              <div class="color-palette-grid">
                <div
                  v-for="element in savedColors"
                  :key="element.id"
                  class="color-card"
                  :style="{
                    backgroundColor: element.color || '#ccc',
                    border: getContrastBorder(element.color || '#ccc'),
                  }"
                  draggable="true"
                  @dragstart="startDrag($event, element)"
                  @click="applyColor(element)"
                  :title="element.name || $t('editor.color')"
                >
                  <div v-if="getEffectivePrice(element) > 0" class="element-price">{{ getEffectivePrice(element) }} ₽</div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- Основные категории - Desktop: сначала выбор категории, затем элементы -->
        <!-- Шаг 1: Grid категорий (если нет выбранной категории) -->
        <template v-if="!isMobile && !selectedDesktopSubcategory && childSubcategories.length === 0">
          <div class="desktop-categories-grid">
            <div 
              v-for="subcategory in filteredCategories" 
              :key="subcategory.id" 
              class="desktop-category-tile"
              @click="selectDesktopSubcategory(subcategory)"
            >
              <div class="desktop-category-icon">
                <img v-if="getSubcategoryPreviewImage(subcategory)" :src="getSubcategoryPreviewImage(subcategory)" alt="" />
                <div v-else class="desktop-category-placeholder"></div>
              </div>
              <span class="desktop-category-name">{{ subcategory.name }}</span>
              <span v-if="!(parseInt(subcategory.children_count) > 0)" class="desktop-category-count">({{ getTotalElementsCount(subcategory) }})</span>
              <span v-if="parseInt(subcategory.children_count) > 0" class="desktop-category-arrow">→</span>
            </div>
          </div>
        </template>

        <!-- Шаг 1.5: Дочерние подкатегории (если выбрана подкатегория с детьми) -->
        <template v-else-if="!isMobile && !selectedDesktopSubcategory && childSubcategories.length > 0">
          <div class="desktop-elements-header">
            <button class="desktop-back-btn" @click.stop="backToDesktopCategories">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <span class="desktop-elements-title">{{ navigationStack.length > 0 ? navigationStack[navigationStack.length - 1].name : '' }}</span>
          </div>
          
          <div v-if="childSubcategoriesLoading" class="elements-loading-skeleton">
            <div class="skeleton-grid desktop-skeleton-grid">
              <div class="skeleton-item" v-for="n in 6" :key="n"></div>
            </div>
          </div>
          
          <div v-else class="desktop-categories-grid">
            <div 
              v-for="subcategory in childSubcategories" 
              :key="subcategory.id" 
              class="desktop-category-tile"
              @click="selectDesktopSubcategory(subcategory)"
            >
              <div class="desktop-category-icon">
                <img v-if="getSubcategoryPreviewImage(subcategory)" :src="getSubcategoryPreviewImage(subcategory)" alt="" />
                <div v-else class="desktop-category-placeholder"></div>
              </div>
              <span class="desktop-category-name">{{ subcategory.name }}</span>
              <span class="desktop-category-count">({{ subcategory.element_count || 0 }})</span>
            </div>
          </div>
        </template>

        <!-- Шаг 2: Элементы выбранной категории (Desktop) -->
        <template v-else-if="!isMobile && selectedDesktopSubcategory">
          <div class="desktop-elements-header">
            <button class="desktop-back-btn" @click.stop="backToDesktopCategories">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <span class="desktop-elements-title">{{ selectedDesktopSubcategory.name }}</span>
          </div>

          <!-- Цвета -->
          <div v-if="isColorCategory(selectedDesktopSubcategory)">
            <div v-if="desktopElementsLoading" class="elements-loading-skeleton">
              <div class="skeleton-grid desktop-skeleton-grid">
                <div class="skeleton-item" v-for="i in 9" :key="'skeleton_' + i"></div>
              </div>
            </div>
            <div v-else class="color-palette-grid">
              <div
                v-for="element in getSubcategoryElements(selectedDesktopSubcategory.id)"
                :key="element.id"
                class="color-card"
                :class="{ 'locked-element': isElementLocked(element) }"
                :style="{
                  backgroundColor: element.color || '#ccc',
                  border: getContrastBorder(element.color || '#ccc'),
                }"
                :draggable="!isElementLocked(element)"
                @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)"
                @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyColor(element)"
                :title="element.name || $t('editor.color')"
              >
                <div v-if="isElementLocked(element)" class="color-lock-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-if="getEffectivePrice(element) > 0" class="element-price">{{ getEffectivePrice(element) }} ₽</div>
              </div>
            </div>
            <div class="custom-color-section">
              <div class="custom-color-picker">
                <span class="custom-color-label">{{ $t('legacy.Выбрать свой цвет') }}</span>
                <label class="color-picker-wrapper">
                  <input type="color" class="color-picker-input" @change="handleCustomColorChange($event)" />
                  <div class="color-preview">
                    <div class="rainbow-gradient"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Шрифты -->
          <div v-else-if="isFontCategory(selectedDesktopSubcategory)" class="font-palette">
            <div v-if="desktopElementsLoading" class="elements-loading-skeleton">
              <div class="skeleton-grid desktop-skeleton-grid">
                <div class="skeleton-item" v-for="i in 6" :key="'skeleton_' + i"></div>
              </div>
            </div>
            <template v-else>
              <div
                v-for="element in getSubcategoryElements(selectedDesktopSubcategory.id)"
                :key="element.id"
                class="font-item"
                :class="{ 'locked-element': isElementLocked(element) }"
                :draggable="!isElementLocked(element)"
                @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)"
                @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyFont(element)"
                :style="{ fontFamily: `'custom_font_${element.id}', sans-serif` }"
              >
                {{ element.name }}
                <div v-if="isElementLocked(element)" class="font-lock-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </template>
          </div>

          <!-- Другие элементы -->
          <template v-else>
            <div v-if="desktopElementsLoading" class="elements-loading-skeleton">
              <div class="skeleton-grid desktop-skeleton-grid">
                <div class="skeleton-item" v-for="i in 9" :key="'skeleton_' + i"></div>
              </div>
            </div>
            
            <div v-else class="desktop-elements-grid" @scroll="handleElementsScroll($event, selectedDesktopSubcategory.id)">
              <!-- Пользовательские элементы -->
              <div 
                class="desktop-element-tile user-element-item" 
                :class="{ 'locked-element': !canAddElements }" 
                v-for="userEl in getUserElementsForCategory(selectedDesktopSubcategory.id)" 
                :key="'user_' + userEl.id"
              >
                <div 
                  class="desktop-element-content" 
                  :draggable="canAddElements" 
                  @dragstart="startDrag($event, { ...userEl, image: userEl.image_url.startsWith('/') ? userEl.image_url : '/' + userEl.image_url, isUserElement: true, rootCategoryName: userEl.category_name })" 
                  @click="applyUserElement(userEl)"
                >
                  <img class="desktop-element-img" :src="userEl.image_url.startsWith('/') ? userEl.image_url : '/' + userEl.image_url" :alt="userEl.name" loading="lazy" />
                  <div v-if="!canAddElements" class="lock-overlay">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
                      <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <button 
                    v-if="canAddElements"
                    class="delete-user-element" 
                    @click="handleDeleteUserElement($event, userEl)"
                    :title="$t('legacy.Удалить элемент')"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div v-if="getEffectivePrice(userEl) > 0" class="element-price">{{ getEffectivePrice(userEl) }} ₽</div>
              </div>
              
              <!-- Обычные элементы -->
              <div 
                class="desktop-element-tile" 
                v-for="element in getSubcategoryElements(selectedDesktopSubcategory.id)" 
                :key="element.id"
              >
                <div 
                  class="desktop-element-content" 
                  :class="{ 'locked-element': isElementLocked(element) }"
                  :draggable="!isElementLocked(element)"
                  @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)" 
                  @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyElement(element)"
                >
                  <img class="desktop-element-img" :src="element.image" :alt="element.name" loading="lazy" />
                  <button 
                    v-if="isElementLocked(element)"
                    class="element-lock-icon"
                    @click.stop="handleLockedElementClick($event, element)"
                    :title="$t('legacy.Доступно по подписке')"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                      <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button 
                    v-else
                    class="favorite-star" 
                    :class="{ 'is-favorite': isFavorite(element.id) }"
                    @click="toggleFavorite($event, element)"
                    :title="$t('legacy.Добавить в избранное')"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                        :fill="isFavorite(element.id) ? '#FFD700' : 'none'" 
                        :stroke="isFavorite(element.id) ? '#FFD700' : 'currentColor'" 
                        stroke-width="2" 
                        stroke-linecap="round" 
                        stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div v-if="getEffectivePrice(element) > 0" class="element-price">{{ getEffectivePrice(element) }} ₽</div>
              </div>
              
              <!-- Индикатор загрузки дополнительных элементов -->
              <div v-if="isLoadingMoreElements(selectedDesktopSubcategory.id)" class="loading-more-indicator">
                <div class="loading-spinner"></div>
              </div>
            </div>
            
            <AppButton v-if="isCustomBackgroundCategory(selectedDesktopSubcategory)" :label="$t('editor.uploadPhoto')" type="button" class="upload-custom-background-btn" @click="triggerCustomBackgroundUpload"/>
            <input v-if="isCustomBackgroundCategory(selectedDesktopSubcategory)" type="file" ref="customBackgroundInput" class="custom-background-file-input" accept="image/*" @change="onCustomBackgroundUpload" />
          </template>
        </template>

        <!-- Mobile: Tile-based навигация (как на десктопе) -->
        <template v-if="isMobile">
          <!-- Заголовок с кнопкой назад -->
          <div v-if="mobileNavStack.length > 0" class="mobile-nav-header">
            <button class="mobile-nav-back-btn" @click="mobileGoBack">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ mobileNavStack[mobileNavStack.length - 1].name }}</span>
            </button>
          </div>

          <!-- View: Подкатегории (первый уровень) -->
          <template v-if="mobileCurrentView === 'subcategories' && filteredCategories.length > 0">
            <div class="mobile-tiles-grid">
              <div 
                v-for="subcategory in filteredCategories" 
                :key="subcategory.id" 
                class="mobile-tile"
                :data-children="subcategory.children_count"
                @click="onMobileSubcategoryTap(subcategory)"
              >
                <div class="mobile-tile-icon">
                  <img v-if="getSubcategoryPreviewImage(subcategory)" :src="getSubcategoryPreviewImage(subcategory)" alt="" />
                  <div v-else class="mobile-tile-placeholder"></div>
                </div>
                <span class="mobile-tile-name">{{ subcategory.name }}</span>
                <span v-if="subcategory.children_count > 0" class="mobile-tile-arrow">{{ subcategory.children_count }}</span>
                <span v-else class="mobile-tile-count">({{ getTotalElementsCount(subcategory) }})</span>
              </div>
            </div>
          </template>

          <!-- View: Дочерние подкатегории -->
          <template v-else-if="mobileCurrentView === 'children'">
            <div v-if="mobileElementsLoading" class="elements-loading-skeleton">
              <div class="skeleton-grid">
                <div class="skeleton-item" v-for="i in 6" :key="'skel_child_' + i"></div>
              </div>
            </div>
            <div v-else-if="(mobileChildSubcategories[mobileSelectedSubcategory?.id] || []).length > 0" class="mobile-tiles-grid">
              <div 
                v-for="child in mobileChildSubcategories[mobileSelectedSubcategory?.id] || []" 
                :key="child.id" 
                class="mobile-tile"
                @click="onMobileChildTap(child)"
              >
                <div class="mobile-tile-icon">
                  <img v-if="getSubcategoryPreviewImage(child)" :src="getSubcategoryPreviewImage(child)" alt="" />
                  <div v-else class="mobile-tile-placeholder"></div>
                </div>
                <span class="mobile-tile-name">{{ child.name }}</span>
                <span v-if="!(parseInt(child.children_count) > 0)" class="mobile-tile-count">({{ child.element_count || 0 }})</span>
              </div>
            </div>
          </template>

          <!-- View: Элементы -->
          <template v-else-if="mobileCurrentView === 'elements'">
            <!-- Цвета -->
            <div v-if="mobileSelectedSubcategory && isColorCategory(mobileSelectedSubcategory)">
              <div v-if="mobileElementsLoading" class="elements-loading-skeleton">
                <div class="skeleton-grid">
                  <div class="skeleton-item" v-for="i in 6" :key="'skel_color_' + i"></div>
                </div>
              </div>
              <template v-else>
                <div class="color-palette-grid">
                  <div
                    v-for="element in getSubcategoryElements(mobileSelectedSubcategory.id)"
                    :key="element.id"
                    class="color-card"
                    :class="{ 'locked-element': isElementLocked(element) }"
                    :style="{
                      backgroundColor: element.color || '#ccc',
                      border: getContrastBorder(element.color || '#ccc'),
                    }"
                    :draggable="!isElementLocked(element)"
                    @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)"
                    @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyColor(element)"
                    :title="element.name || $t('editor.color')"
                  >
                    <div v-if="isElementLocked(element)" class="color-lock-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div v-if="getEffectivePrice(element) > 0" class="element-price">{{ getEffectivePrice(element) }} ₽</div>
                  </div>
                </div>
                <div class="custom-color-section">
                  <div class="custom-color-picker">
                    <span class="custom-color-label">{{ $t('legacy.Выбрать свой цвет') }}</span>
                    <label class="color-picker-wrapper">
                      <input type="color" class="color-picker-input" @change="handleCustomColorChange($event)" />
                      <div class="color-preview">
                        <div class="rainbow-gradient"></div>
                      </div>
                    </label>
                  </div>
                </div>
              </template>
            </div>

            <!-- Шрифты -->
            <div v-else-if="mobileSelectedSubcategory && isFontCategory(mobileSelectedSubcategory)" class="font-palette">
              <div v-if="mobileElementsLoading" class="elements-loading-skeleton">
                <div class="skeleton-grid">
                  <div class="skeleton-item" v-for="i in 6" :key="'skel_font_' + i"></div>
                </div>
              </div>
              <template v-else>
                <div
                  v-for="element in getSubcategoryElements(mobileSelectedSubcategory.id)"
                  :key="element.id"
                  class="font-item"
                  :class="{ 'locked-element': isElementLocked(element) }"
                  :draggable="!isElementLocked(element)"
                  @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)"
                  @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyFont(element)"
                  :style="{ fontFamily: `'custom_font_${element.id}', sans-serif` }"
                >
                  {{ element.name }}
                  <div v-if="isElementLocked(element)" class="font-lock-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                      <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
              </template>
            </div>

            <!-- Обычные элементы -->
            <template v-else>
              <div v-if="mobileElementsLoading" class="elements-loading-skeleton">
                <div class="skeleton-grid">
                  <div class="skeleton-item" v-for="i in 6" :key="'skel_el_' + i"></div>
                </div>
              </div>
              <div v-else class="elements-scroll-container" @scroll="handleElementsScroll($event, mobileSelectedSubcategory?.id)">
                <!-- Пользовательские элементы -->
                <div class="subcategory user-element-item" :class="{ 'locked-element': !canAddElements }" v-for="userEl in getUserElementsForCategory(mobileSelectedSubcategory?.id)" :key="'user_' + userEl.id">
                  <div class="subcategory-header" :draggable="canAddElements" @dragstart="startDrag($event, { ...userEl, image: userEl.image_url.startsWith('/') ? userEl.image_url : '/' + userEl.image_url, isUserElement: true, rootCategoryName: userEl.category_name })" @click="applyUserElement(userEl)">
                    <span>
                      <img class="img-element" :src="userEl.image_url.startsWith('/') ? userEl.image_url : '/' + userEl.image_url" :alt="userEl.name" loading="lazy" />
                    </span>
                    <div v-if="!canAddElements" class="lock-overlay">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <button v-if="canAddElements" class="delete-user-element" @click="handleDeleteUserElement($event, userEl)" @touchend.stop.prevent="handleDeleteUserElement($event, userEl)" :title="$t('legacy.Удалить элемент')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="getEffectivePrice(userEl) > 0" class="element-price">{{ getEffectivePrice(userEl) }} ₽</div>
                </div>
                
                <!-- Обычные элементы -->
                <div class="subcategory" :class="{ 'locked-element': isElementLocked(element) }" v-for="element in getSubcategoryElements(mobileSelectedSubcategory?.id)" :key="element.id">
                  <div class="subcategory-header" :draggable="!isElementLocked(element)" @dragstart="isElementLocked(element) ? $event.preventDefault() : startDrag($event, element)" @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyElement(element)">
                    <span>
                      <img class="img-element" :src="element.image" :alt="element.name" loading="lazy" />
                    </span>
                    <button 
                      v-if="isElementLocked(element)"
                      class="element-lock-icon"
                      @click.stop="handleLockedElementClick($event, element)"
                      @touchend.stop.prevent="handleLockedElementClick($event, element)"
                      :title="$t('legacy.Доступно по подписке')"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      v-else
                      class="favorite-star" 
                      :class="{ 'is-favorite': isFavorite(element.id) }"
                      @click="toggleFavorite($event, element)"
                      @touchend.stop.prevent="toggleFavorite($event, element)"
                      :title="$t('legacy.Добавить в избранное')"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                          :fill="isFavorite(element.id) ? '#FFD700' : 'none'" 
                          :stroke="isFavorite(element.id) ? '#FFD700' : 'currentColor'" 
                          stroke-width="2" 
                          stroke-linecap="round" 
                          stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="getEffectivePrice(element) > 0" class="element-price">{{ getEffectivePrice(element) }} ₽</div>
                </div>
                
                <!-- Индикатор загрузки дополнительных элементов -->
                <div v-if="isLoadingMoreElements(mobileSelectedSubcategory?.id)" class="loading-more-indicator">
                  <div class="loading-spinner"></div>
                </div>
              </div>
              
              <AppButton v-if="mobileSelectedSubcategory && isCustomBackgroundCategory(mobileSelectedSubcategory)" :label="$t('editor.uploadPhoto')" type="button" class="upload-custom-background-btn" @click="triggerCustomBackgroundUpload"/>
              <input v-if="mobileSelectedSubcategory && isCustomBackgroundCategory(mobileSelectedSubcategory)" type="file" ref="customBackgroundInput" class="custom-background-file-input" accept="image/*" @change="onCustomBackgroundUpload" />
            </template>
          </template>
        </template>
        </div>

        <!-- Google Fonts section for Text category -->
        <div v-if="activeIcon === 'Текст'" class="google-fonts-section">
          <!-- User Fonts Section -->
          <template v-if="userFonts.length > 0">
            <div class="fonts-section-header">{{ $t('legacy.Мои шрифты') }}</div>
            <div class="fonts-grid">
              <div
                v-for="font in userFonts"
                :key="'user-' + font.id"
                class="google-font-item user-font-item"
                @click="applyUserFont(font)"
                :style="{ fontFamily: font.font_name }"
              >
                <span class="font-preview">Аа</span>
                <span class="font-name">{{ font.font_name }}</span>
              </div>
            </div>
            <div class="fonts-section-header">Google Fonts</div>
          </template>
          <!-- Google Fonts Section -->
          <div class="fonts-grid">
            <div
              v-for="font in filteredGoogleFonts"
              :key="font.family"
              class="google-font-item"
              @click="applyGoogleFont(font)"
              :style="{ fontFamily: `'${font.family}', ${font.category}` }"
            >
              <span class="font-preview">Аа</span>
              <span class="font-name">{{ font.family }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Модальное окно сохранения цвета -->
      <transition name="modal-fade">
        <div v-if="showColorSaveModal" class="modal-overlay" @click.self="cancelSaveColor">
          <div class="modal-container">
            <div class="modal-header">
              <h3>{{ $t('legacy.Сохранение цвета') }}</h3>
            </div>
            <div class="modal-body">
              <p>{{ $t('legacy.Вы хотите сохранить этот цвет в свою палитру?') }}</p>
              <div
                class="color-preview-modal"
                :style="{
                  backgroundColor: pendingColor?.color,
                  border: getContrastBorder(pendingColor?.color),
                }"
              ></div>
              <input v-if="showColorNameInput" type="text" v-model="pendingColor.name" class="color-name-input" :placeholder="$t('legacy.Название цвета')" />
            </div>
            <div class="modal-footer">
              <button class="modal-btn modal-btn-cancel" @click="cancelSaveColor">{{ $t('legacy.Нет, просто применить') }}</button>
              <button class="modal-btn modal-btn-confirm" @click="confirmSaveColor">{{ $t('legacy.Да, сохранить') }}</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Модальное окно загрузки пользовательского элемента -->
      <transition name="modal-fade">
        <div v-if="showUploadModal" class="modal-overlay" @click.self="closeUploadModal">
          <div class="modal-container upload-modal">
            <div class="modal-header">
              <h3>{{ $t('legacy.Загрузить элемент') }}</h3>
            </div>
            <div class="modal-body">
              <div class="upload-form">
                <div class="upload-field" v-if="!uploadActiveCategory">
                  <label>{{ $t('editor.category') }}</label>
                  <select v-model="uploadParentCategoryId" class="upload-category-select" @change="uploadCategoryId = null">
                    <option :value="null" disabled>{{ $t('legacy.Выберите категорию') }}</option>
                    <option v-for="cat in uploadableParentCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </div>
                <div class="upload-field" v-if="uploadActiveCategory || uploadParentCategoryId">
                  <label>{{ $t('editor.subcategory') }}</label>
                  <select v-model="uploadCategoryId" class="upload-category-select">
                    <option :value="null" disabled>{{ $t('legacy.Выберите подкатегорию') }}</option>
                    <template v-for="item in uploadableSubcategories" :key="item.isGroup ? 'group-' + item.name : item.id">
                      <optgroup v-if="item.isGroup" :label="item.name">
                        <option v-for="child in item.children" :key="child.id" :value="child.id">{{ child.name }}</option>
                      </optgroup>
                      <option v-else :value="item.id">{{ item.name }}</option>
                    </template>
                  </select>
                </div>
                <div class="upload-file-area">
                  <input 
                    type="file" 
                    ref="uploadFileInput" 
                    accept="image/png,image/jpeg,image/webp,image/gif" 
                    @change="handleUploadFileChange" 
                    class="upload-file-input"
                  />
                  <div class="upload-file-preview" v-if="uploadFile">
                    <img :src="getUploadPreviewUrl()" alt="Preview" />
                    <span>{{ uploadFile.name }}</span>
                  </div>
                  <div class="upload-file-placeholder" v-else>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M17 8L12 3L7 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12 3V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>{{ $t('legacy.Выберите изображение') }}</span>
                  </div>
                </div>
                <div class="upload-field">
                  <label>{{ $t('legacy.Название') }}</label>
                  <input type="text" v-model="uploadElementName" :placeholder="$t('legacy.Название элемента')" />
                </div>
                <div class="upload-field">
                  <label>{{ $t('editor.priceRubles') }}</label>
                  <input type="number" v-model="uploadElementPrice" min="0" step="1" placeholder="0" />
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="modal-btn modal-btn-cancel" @click="closeUploadModal">{{ $t('common.cancel') }}</button>
              <button class="modal-btn modal-btn-confirm" @click="submitUploadElement" :disabled="!uploadFile || !uploadCategoryId">{{ $t('common.upload') }}</button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Мобильная шторка категорий/элементов -->
    <div v-if="isMobile && mobileCategorySheetOpen" class="mobile-category-sheet-backdrop" @click="closeMobileCategorySheet"></div>
    <div v-if="isMobile && mobileCategorySheetOpen" class="mobile-category-sheet">
      <div class="mobile-category-sheet-header">
        <button v-if="selectedMobileSubcategory || mobileSheetParentSubcategory" class="mobile-sheet-back-btn" @click="backToSubcategories">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <span class="mobile-sheet-title">{{ mobileSheetTitle }}</span>
        <button class="mobile-sheet-close-btn" @click="closeMobileCategorySheet">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="mobile-category-sheet-content" @scroll="handleMobileSheetScroll">
        <!-- Шаг 1: Подкатегории (или дочерние подкатегории) -->
        <div v-if="!selectedMobileSubcategory" class="mobile-subcategories-grid">
          <div v-if="currentMobileSubcategories.length === 0" class="mobile-empty-message">
            {{ $t('legacy.Подкатегории не найдены') }}
          </div>
          <div 
            v-for="subcategory in currentMobileSubcategories" 
            :key="subcategory.id" 
            class="mobile-subcategory-tile"
            @click="handleMobileSubcategoryClick(subcategory)"
          >
            <div class="mobile-subcategory-icon">
              <img v-if="getSubcategoryPreviewImage(subcategory)" :src="getSubcategoryPreviewImage(subcategory)" alt="" />
              <div v-else class="mobile-subcategory-placeholder"></div>
            </div>
            <span class="mobile-subcategory-name">{{ subcategory.name }}</span>
            <span v-if="parseInt(subcategory.children_count) > 0" class="mobile-subcategory-arrow">→</span>
            <span v-else class="mobile-subcategory-count">({{ getTotalElementsCount(subcategory) }})</span>
          </div>
        </div>

        <!-- Шаг 2: Элементы подкатегории -->
        <div v-else class="mobile-elements-scroll-container" @scroll="handleMobileElementsScroll">
          <div v-if="mobileElementsLoading" class="mobile-skeleton-grid">
            <div class="mobile-skeleton-item" v-for="i in 16" :key="'skel_' + i"></div>
          </div>
          <div v-else class="mobile-elements-grid">
            <!-- Пользовательские элементы -->
            <div 
              v-for="userEl in getUserElementsForCategory(selectedMobileSubcategory?.id)" 
              :key="'user_' + userEl.id"
              class="mobile-element-tile user-element-tile"
              :class="{ 'locked-element': !canAddElements }"
              @click="canAddElements ? applyUserElement(userEl) : null"
            >
              <img :src="userEl.image_url.startsWith('/') ? userEl.image_url : '/' + userEl.image_url" :alt="userEl.name" />
              <div v-if="getEffectivePrice(userEl) > 0" class="mobile-element-price">{{ getEffectivePrice(userEl) }} ₽</div>
              <button 
                v-if="canAddElements"
                class="mobile-delete-user-element"
                @click.stop="handleDeleteUserElement($event, userEl)"
                @touchend.stop.prevent="handleDeleteUserElement($event, userEl)"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <!-- Обычные элементы -->
            <div 
              v-for="element in mobileSubcategoryElements" 
              :key="element.id"
              class="mobile-element-tile"
              :class="{ 'locked-element': isElementLocked(element) }"
              @click="isElementLocked(element) ? handleLockedElementClick($event, element) : applyMobileElement(element)"
            >
              <img v-if="element.image" :src="element.image" :alt="element.name" />
              <div v-else-if="element.color" class="mobile-element-color" :style="{ backgroundColor: element.color }"></div>
              <div v-if="getEffectivePrice(element) > 0" class="mobile-element-price">{{ getEffectivePrice(element) }} ₽</div>
              <button 
                v-if="isElementLocked(element)"
                class="mobile-element-lock"
                @click.stop="handleLockedElementClick($event, element)"
                @touchend.stop.prevent="handleLockedElementClick($event, element)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                  <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button 
                v-else-if="!element.color"
                class="mobile-favorite-star" 
                :class="{ 'is-favorite': isFavorite(element.id) }"
                @click.stop="toggleFavorite($event, element)"
                @touchend.stop.prevent="toggleFavorite($event, element)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                    :fill="isFavorite(element.id) ? '#FFD700' : 'none'" 
                    :stroke="isFavorite(element.id) ? '#FFD700' : 'currentColor'" 
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <div v-if="hasMoreMobileElements" class="mobile-load-more-indicator">
              <div v-if="isLoadingMoreMobileElements" class="mobile-loading-spinner"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { backgroundIcon, collapseRowIcon, glassIcon } from '@/assets/images/category';
import textIcon from '@/assets/images/icons/text-icon.svg';
import { mapActions, mapState, mapGetters } from 'vuex';
import AppButton from '@/components/ui/AppButton.vue';
import CalculatorPanel from '@/components/CalculatorPanel.vue';
import { getFavoriteIds, addFavorite, removeFavorite } from '@/api/favorites';
import { getUserElementsBySubcategory, deleteUserElement, uploadUserElement } from '@/api/user-elements';
import logger from '@/utils/logger';
import { getUserFonts } from '@/api/userFonts';
export default {
  name: 'SidebarMenu',
  components: {
    AppButton,
    CalculatorPanel,
  },
  expose: ['setActiveIcon'],
  props: {
    showContent: {
      type: Boolean,
      default: false,
    },
    showLayersPanel: {
      type: Boolean,
      default: false,
    },
    layersList: {
      type: Array,
      default: () => [],
    },
    groupedLayersList: {
      type: Array,
      default: () => [],
    },
    showCalculatorPanel: {
      type: Boolean,
      default: false,
    },
    calculatorItems: {
      type: Array,
      default: () => [],
    },
    activeItemId: {
      type: [Number, String],
      default: null,
    },
    activeGroup: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      backgroundIcon,
      glassIcon,
      collapseRowIcon,
      textIcon,
      activeIcon: null,
      searchQuery: '',
      savedColors: [],
      showColorSaveModal: false,
      pendingColor: null,
      myColorsOpen: true,
      showColorNameInput: false,
      isMobile: window.innerWidth <= 1024,
      dragItem: null,
      dragLayerIndex: null,
      dragOverLayerIndex: null,
      dragGroupId: null,
      dragGroupChildIndex: null,
      dragOverGroupChildIndex: null,
      categoryLabelRefs: {},
      favorites: {},
      userElements: {},
      userElementsLoading: {},
      categoryElementsLoading: {},
      paginatedElements: {},
      elementsPagination: {},
      elementsLoadingMore: {},
      showUploadModal: false,
      uploadParentCategoryId: null,
      uploadCategoryId: null,
      uploadFile: null,
      uploadElementName: '',
      uploadElementPrice: 0,
      mobileCategorySheetOpen: false,
      selectedMobileSubcategory: null,
      mobileElementsLoading: false,
      mobileSheetParentSubcategory: null,
      mobileSheetChildSubcategories: [],
      selectedDesktopSubcategory: null,
      savedActiveIcon: null,
      desktopElementsLoading: false,
      navigationStack: [],
      childSubcategories: [],
      childSubcategoriesLoading: false,
      mobileChildSubcategories: {},
      mobileChildSubcategoriesLoading: {},
      mobileNavStack: [],
      mobileCurrentView: 'subcategories',
      userFonts: [],
      googleFonts: [
        { family: 'Roboto', category: 'sans-serif' },
        { family: 'Open Sans', category: 'sans-serif' },
        { family: 'Lato', category: 'sans-serif' },
        { family: 'Montserrat', category: 'sans-serif' },
        { family: 'Oswald', category: 'sans-serif' },
        { family: 'Raleway', category: 'sans-serif' },
        { family: 'PT Sans', category: 'sans-serif' },
        { family: 'Roboto Condensed', category: 'sans-serif' },
        { family: 'Noto Sans', category: 'sans-serif' },
        { family: 'Ubuntu', category: 'sans-serif' },
        { family: 'Playfair Display', category: 'serif' },
        { family: 'Merriweather', category: 'serif' },
        { family: 'Lora', category: 'serif' },
        { family: 'PT Serif', category: 'serif' },
        { family: 'Noto Serif', category: 'serif' },
        { family: 'Libre Baskerville', category: 'serif' },
        { family: 'Cormorant Garamond', category: 'serif' },
        { family: 'EB Garamond', category: 'serif' },
        { family: 'Caveat', category: 'handwriting' },
        { family: 'Pacifico', category: 'handwriting' },
        { family: 'Dancing Script', category: 'handwriting' },
        { family: 'Satisfy', category: 'handwriting' },
        { family: 'Great Vibes', category: 'handwriting' },
        { family: 'Lobster', category: 'display' },
        { family: 'Bebas Neue', category: 'display' },
        { family: 'Comfortaa', category: 'display' },
        { family: 'Righteous', category: 'display' },
        { family: 'Russo One', category: 'sans-serif' },
        { family: 'Fira Sans', category: 'sans-serif' },
        { family: 'Nunito', category: 'sans-serif' },
        { family: 'Poppins', category: 'sans-serif' },
        { family: 'Inter', category: 'sans-serif' },
        { family: 'Rubik', category: 'sans-serif' },
        { family: 'Work Sans', category: 'sans-serif' },
        { family: 'Quicksand', category: 'sans-serif' },
        { family: 'Jost', category: 'sans-serif' },
        { family: 'Manrope', category: 'sans-serif' },
      ],
      fontsLoaded: false,
    };
  },
  computed: {
    ...mapState('category', ['categories']),
    ...mapGetters('zones', ['totalProjectPrice']),
    displayLayersList() {
      return this.groupedLayersList.length > 0 ? this.groupedLayersList : this.layersList.map(item => ({ ...item, isGroup: false }));
    },
    canAddElements() {
      return this.$store.getters['subscription/canAddElements'];
    },
    canSeePrices() {
      return this.$store.getters['subscription/canSeePrices'];
    },
    showMyColorsSection() {
      return this.activeIcon === 'Цвета' && this.savedColors.length > 0;
    },
    filteredCategories() {
      if (!this.categories || !Array.isArray(this.categories)) {
        return [];
      }

      const query = this.searchQuery.toLowerCase();
      const sortWithFavorites = (elements) => {
        if (!elements || !Array.isArray(elements)) return [];
        return [...elements].sort((a, b) => {
          const aFav = this.favorites[a.id]?.isFavorite ? 1 : 0;
          const bFav = this.favorites[b.id]?.isFavorite ? 1 : 0;
          return bFav - aFav;
        });
      };

      if (query.length > 0) {
        return this.categories.flatMap((category) => {
          const matchesCategoryName = category.name.toLowerCase().includes(query);

          const matchedSubcategories = (category.subcategories || []).filter((sub) => {
            const matchesSubName = sub.name.toLowerCase().includes(query);
            const elements = Array.isArray(sub.elements) ? sub.elements : [];
            const matchesElement = elements.some((el) => el.name?.toLowerCase().includes(query));
            return matchesCategoryName || matchesSubName || matchesElement;
          });

          return matchedSubcategories.map((sub) => ({
            ...sub,
            elements: sortWithFavorites(sub.elements),
            open: true,
          }));
        });
      }

      const currentCategory = this.categories.find((c) => c.name === this.activeIcon);
      if (!currentCategory) return [];

      if (!currentCategory.subcategories || !Array.isArray(currentCategory.subcategories)) {
        return [];
      }

      return currentCategory.subcategories.map((subcategory) => {
        console.log('[filteredCategories] subcategory:', subcategory.name, 'children_count:', subcategory.children_count);
        return {
          ...subcategory,
          children_count: subcategory.children_count || 0,
          elements: sortWithFavorites(subcategory.elements),
        };
      });
    },
    filteredGoogleFonts() {
      if (!this.searchQuery) return this.googleFonts;
      const query = this.searchQuery.toLowerCase();
      return this.googleFonts.filter(font => 
        font.family.toLowerCase().includes(query) ||
        font.category.toLowerCase().includes(query)
      );
    },
    zoneTotalPrice() {
      return this.layersList.reduce((sum, item) => sum + (Number(item.effectivePrice) || 0), 0);
    },
    uploadActiveCategory() {
      if (!this.activeIcon || this.activeIcon === 'Текст' || this.activeIcon === 'Цвета' || this.activeIcon === 'Фоны') {
        return null;
      }
      return this.categories?.find(c => c.name === this.activeIcon) || null;
    },
    uploadableParentCategories() {
      if (!this.categories || !Array.isArray(this.categories)) return [];
      return this.categories.filter(cat => 
        cat.name !== 'Текст' && cat.name !== 'Цвета' && cat.name !== 'Фоны' &&
        cat.subcategories && cat.subcategories.length > 0
      ).map(cat => ({ id: cat.id, name: cat.name }));
    },
    uploadableSubcategories() {
      const isColorCategory = (sub) => sub.name.toLowerCase().trim() === 'цвета';
      const isFontCategory = (sub) => sub.elements?.some((el) => el.url?.match(/\.(ttf|otf|woff2?)$/i));
      const isCustomBackgroundCategory = (sub) => sub.name.includes('Кастомные фоны');
      
      let parentCategory = this.uploadActiveCategory;
      if (!parentCategory && this.uploadParentCategoryId) {
        parentCategory = this.categories?.find(c => c.id === this.uploadParentCategoryId);
      }
      if (!parentCategory || !parentCategory.subcategories) return [];
      
      const result = [];
      
      for (const sub of parentCategory.subcategories) {
        if (isColorCategory(sub) || isFontCategory(sub) || isCustomBackgroundCategory(sub)) continue;
        
        const hasChildren = sub.subcategories && sub.subcategories.length > 0;
        if (hasChildren) {
          const children = sub.subcategories
            .filter(child => !isColorCategory(child) && !isFontCategory(child) && !isCustomBackgroundCategory(child))
            .map(child => ({ id: child.id, name: child.name }));
          
          if (children.length > 0) {
            result.push({
              isGroup: true,
              name: sub.name,
              children: children
            });
          }
        } else {
          result.push({ 
            isGroup: false,
            id: sub.id, 
            name: sub.name 
          });
        }
      }
      
      return result;
    },
    mobileSheetTitle() {
      if (this.selectedMobileSubcategory) {
        return this.selectedMobileSubcategory.name;
      }
      if (this.mobileSheetParentSubcategory) {
        return this.mobileSheetParentSubcategory.name;
      }
      return this.activeIcon || this.$t('legacy.Выберите категорию');
    },
    mobileSubcategories() {
      const currentCategory = this.categories?.find((c) => c.name === this.activeIcon);
      if (!currentCategory || !currentCategory.subcategories) return [];
      return currentCategory.subcategories;
    },
    currentMobileSubcategories() {
      if (this.mobileSheetChildSubcategories.length > 0) {
        return this.mobileSheetChildSubcategories;
      }
      return this.mobileSubcategories;
    },
    mobileSubcategoryElements() {
      if (!this.selectedMobileSubcategory) return [];
      const subcategoryId = this.selectedMobileSubcategory.id;
      const paginated = this.paginatedElements[subcategoryId];
      if (paginated && paginated.length > 0) return paginated;
      return this.selectedMobileSubcategory.elements || [];
    },
    hasMoreMobileElements() {
      if (!this.selectedMobileSubcategory) return false;
      return this.hasMoreElements(this.selectedMobileSubcategory.id);
    },
    isLoadingMoreMobileElements() {
      if (!this.selectedMobileSubcategory) return false;
      return this.isLoadingMoreElements(this.selectedMobileSubcategory.id);
    },
    uploadableCategories() {
      if (!this.categories || !Array.isArray(this.categories)) return [];
      const result = [];
      const isColorCategory = (sub) => sub.name.toLowerCase().trim() === 'цвета';
      const isFontCategory = (sub) => sub.elements?.some((el) => el.url?.match(/\.(ttf|otf|woff2?)$/i));
      const isCustomBackgroundCategory = (sub) => sub.name.includes('Кастомные фоны');
      this.categories.forEach(cat => {
        if (cat.name === 'Текст' || cat.name === 'Цвета' || cat.name === 'Фоны') return;
        if (cat.subcategories && Array.isArray(cat.subcategories)) {
          cat.subcategories.forEach(sub => {
            if (!isColorCategory(sub) && !isFontCategory(sub) && !isCustomBackgroundCategory(sub)) {
              result.push({ id: sub.id, name: sub.name });
            }
          });
        }
      });
      return result;
    },
  },
  methods: {
    ...mapActions('category', ['fetchCategories']),
    setCategoryLabelRef(el, categoryId) {
      if (el) {
        this.categoryLabelRefs[categoryId] = el;
      }
    },
    adjustCategoryLabelFonts() {
      if (!this.isMobile) return;
      
      this.$nextTick(() => {
        const allLabels = [];
        
        if (this.$refs.categoryLabelText) {
          allLabels.push(this.$refs.categoryLabelText);
        }
        
        Object.values(this.categoryLabelRefs).forEach(el => {
          if (el) allLabels.push(el);
        });
        
        allLabels.forEach(label => {
          if (!label) return;
          
          label.style.fontSize = '';
          
          const parent = label.closest('.icon-item');
          if (!parent) return;
          
          const maxWidth = parent.offsetWidth - 8;
          let fontSize = 10;
          const minFontSize = 6;
          
          label.style.fontSize = fontSize + 'px';
          label.style.whiteSpace = 'nowrap';
          
          while (label.scrollWidth > maxWidth && fontSize > minFontSize) {
            fontSize -= 0.5;
            label.style.fontSize = fontSize + 'px';
          }
        });
      });
    },
    getLayerName(item) {
      if (item.isText || item.type === 'text') {
        return item.text ? (item.text.length > 20 ? item.text.substring(0, 20) + '...' : item.text) : this.$t('legacy.Текст');
      }
      if (item.name) {
        return item.name;
      }
      if (item._isBackground || item.isBackground) {
        return this.$t('legacy.Фон');
      }
      return `${this.$t('editor.element')} ${item.id}`;
    },
    handleLayerDragStart(event, index) {
      this.dragLayerIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());
    },
    handleLayerDragEnd() {
      this.dragLayerIndex = null;
      this.dragOverLayerIndex = null;
    },
    handleLayerDragOver(event, index) {
      if (this.dragLayerIndex === null) return;
      this.dragOverLayerIndex = index;
    },
    handleLayerDragLeave() {
      this.dragOverLayerIndex = null;
    },
    handleLayerDrop(targetIndex) {
      if (this.dragLayerIndex === null || this.dragLayerIndex === targetIndex) {
        this.handleLayerDragEnd();
        return;
      }
      const newOrder = [...this.displayLayersList];
      const [movedItem] = newOrder.splice(this.dragLayerIndex, 1);
      newOrder.splice(targetIndex, 0, movedItem);
      this.$emit('reorder-layers', newOrder.map(item => item.id));
      this.handleLayerDragEnd();
    },
    handleGroupChildDragStart(event, groupId, childIndex) {
      this.dragGroupId = groupId;
      this.dragGroupChildIndex = childIndex;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', `${groupId}:${childIndex}`);
      event.stopPropagation();
    },
    handleGroupChildDragEnd() {
      this.dragGroupId = null;
      this.dragGroupChildIndex = null;
      this.dragOverGroupChildIndex = null;
    },
    handleGroupChildDragOver(event, groupId, childIndex) {
      if (this.dragGroupId !== groupId || this.dragGroupChildIndex === null) return;
      this.dragOverGroupChildIndex = childIndex;
      event.stopPropagation();
    },
    handleGroupChildDragLeave() {
      this.dragOverGroupChildIndex = null;
    },
    handleGroupChildDrop(groupId, targetIndex) {
      if (this.dragGroupId !== groupId || this.dragGroupChildIndex === null || this.dragGroupChildIndex === targetIndex) {
        this.handleGroupChildDragEnd();
        return;
      }
      this.$emit('reorder-group-children', groupId, this.dragGroupChildIndex, targetIndex);
      this.handleGroupChildDragEnd();
    },
    triggerCustomBackgroundUpload() {
      if (this.$refs.customBackgroundInput && this.$refs.customBackgroundInput[0]) {
        this.$refs.customBackgroundInput[0].click();
      }
    },
    async onCustomBackgroundUpload(e) {
      const file = e.target.files[0];

      if (!file || !file.type.match('image.*')) {
        e.target.value = '';
        return;
      }

      const formData = new FormData();
      formData.append('imageFile', file);

      try {
        await this.$store.dispatch('customBackground/uploadCustomBackgroundAction', formData);
      } catch (error) {
        console.error('Ошибка загрузки:', error);
      }
    },
                startDrag(event, element) {
                        // Блокировка drag-and-drop для пользовательских элементов без активной подписки
                        if (element.isUserElement && !this.canAddElements) {
                                event.preventDefault();
                                this.$emit('show-subscription-required', {
                                        message: this.$t('legacy.Для использования загруженных объектов необходима активная подписка')
                                });
                                return;
                        }
                        
                        if (element.color || element.image) {
                                this.dragItem = {
                                        ...element,
                                };
                        } else {
                                this.dragItem = {
                                        ...element,
                                        width: 200,
                                        height: 40,
                                };
                        }
                        event.dataTransfer.setData('text/plain', JSON.stringify(element));
                        event.dataTransfer.effectAllowed = 'copy';

                        const dragImage = document.createElement('div');
                        dragImage.style.width = '100px';
                        dragImage.style.height = '100px';
                        dragImage.style.backgroundColor = 'transparent';
                        dragImage.style.position = 'absolute';
                        dragImage.style.top = '-1000px';

                        document.body.appendChild(dragImage);

                        const content = document.createElement('div');
                        content.style.width = '100%';
                        content.style.height = '100%';
                        content.style.display = 'flex';
                        content.style.justifyContent = 'center';
                        content.style.alignItems = 'center';
                        content.style.overflow = 'hidden';

                        if (element.color) {
                                // Для цветных элементов
                                const colorBox = document.createElement('div');
                                colorBox.style.width = '80px';
                                colorBox.style.height = '80px';
                                colorBox.style.backgroundColor = element.color;
                                colorBox.style.borderRadius = '8px';
                                colorBox.style.border = '1px solid rgba(0,0,0,0.2)';
                                content.appendChild(colorBox);
                        } else if (element.image) {
                                // Для изображений
                                const img = new Image();
                                img.src = element.image;
                                
                                img.onload = () => {
                                        content.appendChild(img);
                                        dragImage.appendChild(content);
                                        event.dataTransfer.setDragImage(dragImage, 50, 50);
                                };

                                img.onerror = () => {
                                        content.appendChild(img);
                                        dragImage.appendChild(content);
                                        event.dataTransfer.setDragImage(dragImage, 50, 50);
                                };
                        } else {
                                // Для текста
                                const text = document.createElement('div');
                                text.textContent = element.text || 'Text';
                                text.style.fontSize = '16px';
                                text.style.color = '#333';
                                content.appendChild(text);
                        }

                        if (!element.image) {
                                dragImage.appendChild(content);
                                event.dataTransfer.setDragImage(dragImage, 50, 50);
                        }

                        setTimeout(() => {
                                document.body.removeChild(dragImage);
                        }, 0);
                },
    closeAllCategories() {
      this.myColorsOpen = false;
      if (this.categories && Array.isArray(this.categories)) {
        this.categories.forEach((category) => {
          if (category.subcategories && Array.isArray(category.subcategories)) {
            category.subcategories.forEach((sub) => {
              sub.open = false;
            });
          }
        });
      }
      this.activeIcon = null;
    },
    handleClickOutside(event) {
      if (this.mobileCategorySheetOpen) return;
      if (this.selectedDesktopSubcategory) return;
      const sidebar = this.$el;
      if (!sidebar.contains(event.target)) {
        this.closeAllCategories();
        this.$emit('close-sidebar');
      }
    },
    getContrastBorder(color) {
      if (!color) return '1px solid #e0e0e0';

      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      return brightness > 128 ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(255, 255, 255, 0.5)';
    },
    setActiveIcon(name) {
      this.activeIcon = name;
      this.selectedDesktopSubcategory = null;
      this.navigationStack = [];
      this.childSubcategories = [];
      this.$emit('icon-change', name);
      if (this.isMobile && name !== 'Текст') {
        this.openMobileCategorySheet();
      }
    },
    openMobileCategorySheet() {
      this.selectedMobileSubcategory = null;
      this.mobileCategorySheetOpen = true;
    },
    closeMobileCategorySheet() {
      this.mobileCategorySheetOpen = false;
      this.selectedMobileSubcategory = null;
      this.mobileSheetParentSubcategory = null;
      this.mobileSheetChildSubcategories = [];
    },
    backToSubcategories() {
      if (this.selectedMobileSubcategory) {
        this.selectedMobileSubcategory = null;
        return;
      }
      if (this.mobileSheetParentSubcategory) {
        this.mobileSheetParentSubcategory = null;
        this.mobileSheetChildSubcategories = [];
      }
    },
    async selectDesktopSubcategory(subcategory) {
      this.savedActiveIcon = this.activeIcon;
      
      const hasChildren = parseInt(subcategory.children_count) > 0;
      
      if (hasChildren) {
        this.navigationStack.push(subcategory);
        this.selectedDesktopSubcategory = null;
        await this.loadChildSubcategories(subcategory.id);
      } else {
        this.selectedDesktopSubcategory = subcategory;
        this.desktopElementsLoading = true;
        Promise.all([
          this.loadCategoryElements(subcategory.id),
          this.loadUserElements(subcategory.id)
        ]).finally(() => {
          this.desktopElementsLoading = false;
        });
      }
    },
    async loadChildSubcategories(parentId) {
      this.childSubcategoriesLoading = true;
      try {
        const { getChildSubcategories } = await import('@/api/category');
        const response = await getChildSubcategories(parentId);
        if (response.success) {
          this.childSubcategories = response.subcategories.map(sub => ({
            ...sub,
            children_count: parseInt(sub.children_count) || 0,
            element_count: parseInt(sub.element_count) || 0
          }));
        }
      } catch (error) {
        console.error('Error loading child subcategories:', error);
        this.childSubcategories = [];
      } finally {
        this.childSubcategoriesLoading = false;
      }
    },
    backToDesktopCategories() {
      if (this.selectedDesktopSubcategory) {
        this.selectedDesktopSubcategory = null;
        if (this.navigationStack.length > 0) {
          return;
        }
      }
      
      if (this.navigationStack.length > 0) {
        this.navigationStack.pop();
        if (this.navigationStack.length > 0) {
          const parent = this.navigationStack[this.navigationStack.length - 1];
          this.loadChildSubcategories(parent.id);
        } else {
          this.childSubcategories = [];
        }
        return;
      }
      
      if (this.savedActiveIcon) {
        this.activeIcon = this.savedActiveIcon;
      }
      this.childSubcategories = [];
    },
    selectMobileSubcategory(subcategory) {
      this.selectedMobileSubcategory = subcategory;
      this.mobileElementsLoading = true;
      Promise.all([
        this.loadCategoryElements(subcategory.id),
        this.loadUserElements(subcategory.id)
      ]).finally(() => {
        this.mobileElementsLoading = false;
      });
    },
    async handleMobileSubcategoryClick(subcategory) {
      const hasChildren = parseInt(subcategory.children_count) > 0;
      console.log('[Mobile Sheet] Click subcategory:', subcategory.name, 'children_count:', subcategory.children_count, 'hasChildren:', hasChildren);
      
      if (hasChildren) {
        this.mobileSheetParentSubcategory = subcategory;
        this.mobileElementsLoading = true;
        try {
          const children = await this.$store.dispatch('category/fetchChildSubcategories', subcategory.id);
          console.log('[Mobile Sheet] Loaded children:', children);
          this.mobileSheetChildSubcategories = children || [];
        } catch (e) {
          console.error('[Mobile Sheet] Error loading children:', e);
          this.mobileSheetChildSubcategories = [];
        }
        this.mobileElementsLoading = false;
      } else {
        this.mobileSheetChildSubcategories = [];
        this.mobileSheetParentSubcategory = null;
        this.selectMobileSubcategory(subcategory);
      }
    },
    getSubcategoryPreviewImage(subcategory) {
      if (subcategory.cover_image) {
        return subcategory.cover_image;
      }
      if (subcategory.first_element_image) {
        return subcategory.first_element_image;
      }
      if (subcategory.elements && subcategory.elements.length > 0) {
        const firstWithImage = subcategory.elements.find(el => el.image);
        if (firstWithImage) return firstWithImage.image;
      }
      return null;
    },
    applyMobileElement(element) {
      if (element.color) {
        this.applyColor(element);
      } else {
        this.applyElement(element);
      }
      this.closeMobileCategorySheet();
    },
    toggleCategory(id) {
      if (this.categories && Array.isArray(this.categories)) {
        this.categories.forEach((category) => {
          if (category.subcategories && Array.isArray(category.subcategories)) {
            const subcategory = category.subcategories.find((sub) => sub.id === id);
            if (subcategory) {
              subcategory.open = !subcategory.open;
              if (subcategory.open && !this.isColorCategory(subcategory) && !this.isFontCategory(subcategory)) {
                const hasChildren = parseInt(subcategory.children_count) > 0;
                if (hasChildren) {
                  this.loadMobileChildSubcategories(id);
                } else {
                  this.loadCategoryElements(id);
                  this.loadUserElements(id);
                }
              }
            }
          }
        });
      }
    },
    async loadMobileChildSubcategories(parentId) {
      if (this.mobileChildSubcategories[parentId]) return;
      
      this.mobileChildSubcategoriesLoading = { ...this.mobileChildSubcategoriesLoading, [parentId]: true };
      try {
        const { getChildSubcategories } = await import('@/api/category');
        const response = await getChildSubcategories(parentId);
        if (response.success) {
          this.mobileChildSubcategories = {
            ...this.mobileChildSubcategories,
            [parentId]: response.subcategories.map(sub => ({
              ...sub,
              children_count: parseInt(sub.children_count) || 0,
              element_count: parseInt(sub.element_count) || 0,
              open: false
            }))
          };
        }
      } catch (error) {
        console.error('Error loading mobile child subcategories:', error);
        this.mobileChildSubcategories = { ...this.mobileChildSubcategories, [parentId]: [] };
      } finally {
        this.mobileChildSubcategoriesLoading = { ...this.mobileChildSubcategoriesLoading, [parentId]: false };
      }
    },
    toggleMobileChildSubcategory(parentId, childId) {
      const children = this.mobileChildSubcategories[parentId];
      if (!children) return;
      
      const child = children.find(c => c.id === childId);
      if (child) {
        child.open = !child.open;
        if (child.open) {
          const hasNestedChildren = parseInt(child.children_count) > 0;
          if (hasNestedChildren) {
            this.loadMobileChildSubcategories(childId);
          } else {
            this.loadCategoryElements(childId);
            this.loadUserElements(childId);
          }
        }
      }
    },
    toggleNestedSubcategory(parentId, nestedId) {
      const nestedList = this.mobileChildSubcategories[parentId];
      if (!nestedList) return;
      
      const nested = nestedList.find(n => n.id === nestedId);
      if (nested) {
        nested.open = !nested.open;
        if (nested.open) {
          this.loadCategoryElements(nestedId);
          this.loadUserElements(nestedId);
        }
      }
    },
    async onMobileSubcategoryTap(subcategory) {
      const hasChildren = parseInt(subcategory.children_count) > 0;
      console.log('[Mobile Nav] Tap subcategory:', subcategory.name, 'id:', subcategory.id, 'children_count:', subcategory.children_count, 'hasChildren:', hasChildren);
      
      this.mobileNavStack.push({ 
        name: subcategory.name, 
        prevView: this.mobileCurrentView, 
        prevSubcategory: this.mobileSelectedSubcategory 
      });
      
      if (this.isColorCategory(subcategory) || this.isFontCategory(subcategory)) {
        this.mobileSelectedSubcategory = subcategory;
        this.mobileCurrentView = 'elements';
        this.mobileElementsLoading = true;
        await Promise.all([
          this.loadCategoryElements(subcategory.id),
          this.loadUserElements(subcategory.id)
        ]);
        this.mobileElementsLoading = false;
        return;
      }
      
      if (hasChildren) {
        console.log('[Mobile Nav] Setting view to children for:', subcategory.name);
        this.mobileSelectedSubcategory = subcategory;
        this.mobileCurrentView = 'children';
        this.mobileElementsLoading = true;
        await this.loadMobileChildSubcategories(subcategory.id);
        console.log('[Mobile Nav] Loaded children:', this.mobileChildSubcategories[subcategory.id]);
        this.mobileElementsLoading = false;
      } else {
        this.mobileSelectedSubcategory = subcategory;
        this.mobileCurrentView = 'elements';
        this.mobileElementsLoading = true;
        await Promise.all([
          this.loadCategoryElements(subcategory.id),
          this.loadUserElements(subcategory.id)
        ]);
        this.mobileElementsLoading = false;
      }
    },
    async onMobileChildTap(child) {
      const hasChildren = parseInt(child.children_count) > 0;
      
      this.mobileNavStack.push({ 
        name: child.name, 
        prevView: this.mobileCurrentView, 
        prevSubcategory: this.mobileSelectedSubcategory 
      });
      
      if (hasChildren) {
        this.mobileSelectedSubcategory = child;
        this.mobileCurrentView = 'children';
        this.mobileElementsLoading = true;
        await this.loadMobileChildSubcategories(child.id);
        this.mobileElementsLoading = false;
      } else {
        this.mobileSelectedSubcategory = child;
        this.mobileCurrentView = 'elements';
        this.mobileElementsLoading = true;
        await Promise.all([
          this.loadCategoryElements(child.id),
          this.loadUserElements(child.id)
        ]);
        this.mobileElementsLoading = false;
      }
    },
    mobileGoBack() {
      if (this.mobileNavStack.length === 0) return;
      
      const entry = this.mobileNavStack.pop();
      this.mobileCurrentView = entry.prevView;
      this.mobileSelectedSubcategory = entry.prevSubcategory;
    },
    async loadCategoryElements(categoryId) {
      if (this.categoryElementsLoading[categoryId]) return;
      if (this.paginatedElements[categoryId]) return;
      
      this.categoryElementsLoading = { ...this.categoryElementsLoading, [categoryId]: true };
      
      try {
        const { getSubcategoryElements } = await import('@/api/category');
        const result = await getSubcategoryElements(categoryId, 1, 20);
        
        if (result.success) {
          this.paginatedElements = { 
            ...this.paginatedElements, 
            [categoryId]: result.elements
          };
          this.elementsPagination = { 
            ...this.elementsPagination, 
            [categoryId]: result.pagination 
          };
        }
      } catch (error) {
        console.error('Error loading elements:', error);
      } finally {
        this.categoryElementsLoading = { ...this.categoryElementsLoading, [categoryId]: false };
      }
    },
    async loadMoreElements(categoryId) {
      if (this.elementsLoadingMore[categoryId]) return;
      
      const pagination = this.elementsPagination[categoryId];
      if (!pagination || !pagination.hasMore) return;
      
      this.elementsLoadingMore = { ...this.elementsLoadingMore, [categoryId]: true };
      
      try {
        const { getSubcategoryElements } = await import('@/api/category');
        const result = await getSubcategoryElements(categoryId, pagination.page + 1, 20);
        
        if (result.success) {
          const currentElements = this.paginatedElements[categoryId] || [];
          this.paginatedElements = { 
            ...this.paginatedElements, 
            [categoryId]: [...currentElements, ...result.elements]
          };
          this.elementsPagination = { 
            ...this.elementsPagination, 
            [categoryId]: result.pagination 
          };
        }
      } catch (error) {
        console.error('Error loading more elements:', error);
      } finally {
        this.elementsLoadingMore = { ...this.elementsLoadingMore, [categoryId]: false };
      }
    },
    getSubcategoryElements(subcategoryId) {
      return this.paginatedElements[subcategoryId] || [];
    },
    hasMoreElements(subcategoryId) {
      const pagination = this.elementsPagination[subcategoryId];
      return pagination && pagination.hasMore;
    },
    isLoadingMoreElements(subcategoryId) {
      return this.elementsLoadingMore[subcategoryId] || false;
    },
    handleElementsScroll(event, subcategoryId) {
      const container = event.target;
      const threshold = 100;
      
      if (container.scrollHeight - container.scrollTop - container.clientHeight < threshold) {
        this.loadMoreElements(subcategoryId);
      }
    },
    handleMobileElementsScroll(event) {
      if (!this.selectedMobileSubcategory) return;
      const container = event.target;
      const threshold = 100;
      
      if (container.scrollHeight - container.scrollTop - container.clientHeight < threshold) {
        this.loadMoreElements(this.selectedMobileSubcategory.id);
      }
    },
    handleMobileSheetScroll(event) {
      // Срабатывает при скролле шторки с элементами
      if (!this.selectedMobileSubcategory) return;
      const container = event.target;
      const threshold = 150;
      
      if (container.scrollHeight - container.scrollTop - container.clientHeight < threshold) {
        this.loadMoreElements(this.selectedMobileSubcategory.id);
      }
    },
    isCategoryLoading(categoryId) {
      return this.categoryElementsLoading[categoryId] || this.userElementsLoading[categoryId] || false;
    },
    toggleMyColors() {
      this.myColorsOpen = !this.myColorsOpen;
    },
    isColorCategory(subcategory) {
      const name = subcategory.name.toLowerCase().trim();
      return name === 'цвета';
    },
    isCustomBackgroundCategory(subcategory) {
      return subcategory.name.includes('Кастомные фоны');
    },
    isFontCategory(subcategory) {
      return subcategory.elements?.some((el) => el.url?.match(/\.(ttf|otf|woff2?)$/i));
    },
    applyColor(color) {
      if (this.isMobile) this.closeAllCategories();
      this.$emit('add-image', color);
    },
    applyCustomColor(color) {
      this.$emit('add-image', { ...color, isCustom: true });
    },
    applyFont(font) {
                        if (this.isMobile) this.closeAllCategories();
      this.$emit('add-font', {
        name: font.name,
        url: font.url,
        fontId: font.id,
      });
    },
    applyGoogleFont(font) {
      if (this.isMobile) this.closeAllCategories();
      this.$emit('add-font', {
        name: font.family,
        fontFamily: font.family,
        category: font.category,
        isGoogleFont: true,
      });
    },
    loadGoogleFonts() {
      if (this.fontsLoaded) return;
      
      const fontFamilies = this.googleFonts.map(f => f.family.replace(/ /g, '+')).join('|');
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies.split('|').map(f => f + ':wght@400;700').join('&family=')}&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      
      this.fontsLoaded = true;
    },
    async loadUserFonts() {
      try {
        const response = await getUserFonts();
        if (response.success && response.fonts) {
          this.userFonts = response.fonts;
          for (const font of this.userFonts) {
            await this.loadUserFontFace(font);
          }
        }
      } catch (error) {
        console.error('Error loading user fonts:', error);
        this.userFonts = [];
      }
    },
    async loadUserFontFace(font) {
      try {
        const fontFace = new FontFace(font.font_name, `url(${font.font_url})`);
        await fontFace.load();
        document.fonts.add(fontFace);
      } catch (error) {
        console.error(`Error loading font face for ${font.font_name}:`, error);
      }
    },
    applyUserFont(font) {
      if (this.isMobile) this.closeAllCategories();
      this.$emit('add-text', {
        name: 'Текст',
        type: 'text',
        fontName: font.font_name,
        isUserFont: true,
        isGoogleFont: false,
      });
    },
    applyElement(element) {
      if (this.isMobile) this.closeAllCategories();
      this.$emit('add-image', element);
    },
    handleCustomColorChange(event) {
      const selectedColor = event.target.value;
      this.pendingColor = {
        id: `custom-color-${Date.now()}`,
        color: selectedColor,
        name: this.generateColorName(selectedColor),
        isColor: true,
      };
      this.showColorSaveModal = true;
      this.showColorNameInput = true;
    },
    generateColorName(hexColor) {
      const colorMap = {
        '#FF0000': this.$t('legacy.Красный'),
        '#00FF00': this.$t('legacy.Зеленый'),
        '#0000FF': this.$t('legacy.Синий'),
        '#FFFF00': this.$t('legacy.Желтый'),
        '#FF00FF': this.$t('legacy.Пурпурный'),
        '#00FFFF': this.$t('legacy.Бирюзовый'),
        '#FFFFFF': this.$t('legacy.Белый'),
        '#000000': this.$t('legacy.Черный'),
      };
      return colorMap[hexColor.toUpperCase()] || `${this.$t('editor.color')} ${hexColor}`;
    },
    confirmSaveColor() {
      if (!this.pendingColor) return;

      if (!this.pendingColor.name.trim()) {
        this.pendingColor.name = this.generateColorName(this.pendingColor.color);
      }

      this.savedColors.push(this.pendingColor);
      this.saveColorsToLocalStorage();
      this.$emit('add-image', this.pendingColor);
      this.showColorSaveModal = false;
      this.pendingColor = null;
      this.showColorNameInput = false;
    },
    cancelSaveColor() {
      if (this.pendingColor) {
        this.$emit('add-image', this.pendingColor);
      }
      this.showColorSaveModal = false;
      this.pendingColor = null;
      this.showColorNameInput = false;
    },
    saveColorsToLocalStorage() {
      localStorage.setItem('savedColors', JSON.stringify(this.savedColors));
    },
    loadColorsFromLocalStorage() {
      const colors = localStorage.getItem('savedColors');
      if (colors) {
        this.savedColors = JSON.parse(colors);
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
      this.adjustCategoryLabelFonts();
    },
    async loadFavorites() {
      try {
        this.favorites = await getFavoriteIds();
      } catch (error) {
        console.error('Error loading favorites:', error);
        this.favorites = {};
      }
    },
    isFavorite(elementId) {
      return this.favorites[elementId]?.isFavorite || false;
    },
    isElementLocked(element) {
      if (!element) return false;
      // Use server-provided isLocked flag (set for free users on paid elements)
      // or fallback to local check for backward compatibility
      if (element.isLocked !== undefined) {
        return element.isLocked;
      }
      return !this.canAddElements && element.is_free === false;
    },
    handleLockedElementClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.$emit('show-subscription-required', {
        message: this.$t('legacy.Этот элемент доступен только по подписке')
      });
    },
    getEffectivePrice(element) {
      if (!element) return 0;
      const key = String(element.id);
      const fav = this.favorites[key];
      if (fav?.customPrice !== null && fav?.customPrice !== undefined) {
        return Number(fav.customPrice);
      }
      return element.price || 0;
    },
    async toggleFavorite(event, element) {
      event.stopPropagation();
      event.preventDefault();
      try {
        if (this.favorites[element.id]?.isFavorite) {
          await removeFavorite(element.id);
          delete this.favorites[element.id];
          this.favorites = { ...this.favorites };
        } else {
          await addFavorite(element.id);
          this.favorites = {
            ...this.favorites,
            [element.id]: { isFavorite: true, customPrice: null }
          };
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
      }
    },
    async loadUserElements(categoryId) {
      if (this.userElementsLoading[categoryId]) return;
      this.userElementsLoading[categoryId] = true;
      try {
        const elements = await getUserElementsBySubcategory(categoryId);
        this.userElements = { ...this.userElements, [categoryId]: elements };
      } catch (error) {
        console.error('Error loading user elements:', error);
        logger.captureError(error, {
          component: 'user-element-upload',
          action: 'loadUserElements',
          categoryId
        });
        this.userElements[categoryId] = [];
      } finally {
        this.userElementsLoading[categoryId] = false;
      }
    },
    getUserElementsForCategory(categoryId) {
      return this.userElements[categoryId] || [];
    },
    getTotalElementsCount(subcategory) {
      const pagination = this.elementsPagination[subcategory.id];
      const baseCount = pagination ? pagination.total : (subcategory.elements ? subcategory.elements.length : 0);
      const userCount = this.getUserElementsForCategory(subcategory.id).length;
      return baseCount + userCount;
    },
    async handleDeleteUserElement(event, element) {
      event.stopPropagation();
      event.preventDefault();
      if (!confirm(this.$t('legacy.Удалить этот элемент?'))) return;
      try {
        await deleteUserElement(element.id);
        const categoryId = element.category_id;
        this.userElements[categoryId] = this.userElements[categoryId].filter(e => e.id !== element.id);
        this.userElements = { ...this.userElements };
      } catch (error) {
        console.error('Error deleting user element:', error);
      }
    },
    applyUserElement(element) {
      // Блокировка для пользователей с истекшей подпиской
      if (!this.canAddElements) {
        this.$emit('show-subscription-required', {
          message: this.$t('legacy.Для использования загруженных объектов необходима активная подписка')
        });
        return;
      }
      
      const imageElement = {
        id: `user_${element.id}`,
        name: element.name,
        image: element.image_url.startsWith('/') ? element.image_url : `/${element.image_url}`,
        price: element.price || 0,
        isUserElement: true,
        userElementId: element.id,
        rootCategoryName: element.category_name || null,
      };
      this.$emit('add-image', imageElement);
      if (this.isMobile) {
        this.closeAllCategories();
        this.$emit('close-sidebar');
      }
    },
    handleUploadClick() {
      if (!this.canAddElements) {
        this.$router.push('/subscription/plans');
        return;
      }
      this.openUploadModal(null);
    },
    openUploadModal(categoryId) {
      this.uploadParentCategoryId = null;
      this.uploadCategoryId = categoryId;
      this.uploadFile = null;
      this.uploadElementName = '';
      this.uploadElementPrice = 0;
      this.showUploadModal = true;
    },
    closeUploadModal() {
      this.showUploadModal = false;
      this.uploadParentCategoryId = null;
      this.uploadCategoryId = null;
      this.uploadFile = null;
      this.uploadElementName = '';
      this.uploadElementPrice = 0;
    },
    handleUploadFileChange(event) {
      this.uploadFile = event.target.files[0];
      if (this.uploadFile && !this.uploadElementName) {
        this.uploadElementName = this.uploadFile.name.replace(/\.[^/.]+$/, '');
      }
    },
    getUploadPreviewUrl() {
      if (this.uploadFile) {
        return URL.createObjectURL(this.uploadFile);
      }
      return '';
    },
    async submitUploadElement() {
      if (!this.uploadFile || !this.uploadCategoryId) return;
      try {
        const newElement = await uploadUserElement(
          this.uploadCategoryId,
          this.uploadFile,
          this.uploadElementName,
          this.uploadElementPrice
        );
        if (!this.userElements[this.uploadCategoryId]) {
          this.userElements[this.uploadCategoryId] = [];
        }
        this.userElements[this.uploadCategoryId].unshift(newElement);
        this.userElements = { ...this.userElements };
        this.closeUploadModal();
      } catch (error) {
        console.error('Error uploading element:', error);
        logger.captureError(error, {
          component: 'user-element-upload',
          categoryId: this.uploadCategoryId,
          fileName: this.uploadFile?.name,
          fileSize: this.uploadFile?.size
        });
        alert(this.$t('legacy.Ошибка загрузки элемента'));
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.isMobile = window.innerWidth <= 1024;
    
    window.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
    this.fetchCategories().then(() => {
      this.$nextTick(() => {
        this.adjustCategoryLabelFonts();
      });
    });
    this.loadColorsFromLocalStorage();
    this.loadGoogleFonts();
    this.loadUserFonts();
    this.loadFavorites();

    // Добавляем обработчики для перетаскивания
    document.addEventListener('dragover', (e) => {
      e.preventDefault(); // Необходимо для работы drop
    });

    document.addEventListener('drop', (e) => {
      e.preventDefault();
                        if (this.dragItem) {

        const defaultSize = 100;

        const rect = document.querySelector('.content-section').getBoundingClientRect();
        const x = e.clientX - rect.left - defaultSize / 2;
        const y = e.clientY - rect.top - defaultSize / 2;

        this.dragItem.x = x;
        this.dragItem.y = y;

                                this.$emit('add-image', this.dragItem);
                                this.$emit('add-font', {
                                        name: this.dragItem.name,
                                        url: this.dragItem.url,
                                        fontId: this.dragItem.id,
                                        width: this.dragItem.width,
                                        height: this.dragItem.height,
                                        x: this.dragItem.x,
                                        y: this.dragItem.y
                                });
        this.dragItem = null;
        
        if (this.isMobile) {
          this.closeAllCategories();
          this.$emit('close-sidebar');
        }
      }
    });
  },
};
</script>

<style scoped>
.custom-background-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  border: 0;
  clip: rect(0 0 0 0);
}

.upload-custom-background-btn {
  grid-column: 1 / -1;
  width: 100%;
}

.google-fonts-section {
  padding: 8px 0;
}

.fonts-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.google-font-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.google-font-item:hover {
  background-color: #edf2f7;
  border-color: #496782;
  transform: translateX(4px);
}

.google-font-item .font-preview {
  font-size: 28px;
  color: #2d3748;
  min-width: 45px;
  text-align: center;
  font-family: inherit;
}

.google-font-item .font-name {
  font-size: 14px;
  color: #4a5568;
  font-family: inherit;
}

.fonts-section-header {
  padding: 12px 16px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-font-item {
  background: #FEF3C7 !important;
  border-left: 3px solid #F59E0B !important;
  border-color: #F59E0B !important;
}

.user-font-item:hover {
  background: #FDE68A !important;
}

.text-elements {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  padding: 12px;
}

.element-price {
  text-align: center;
  font-size: 12px;
  color: #444;
  margin-top: 6px;
  padding-bottom: 2px;
  user-select: none;
  white-space: nowrap;
}

.color-card-wrapper,
.font-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.text-element {
  padding: 12px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  font-size: 14px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.text-element:hover {
  background-color: #edf2f7;
  transform: translateY(-2px);
}

/* Стили для обычных элементов и фонов */
.default-elements {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  padding: 12px;
}

.default-element {
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.default-element img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.default-element:hover img {
  transform: scale(1.05);
}

.sidebar-menu {
  --sidebar-width: 94px;
  --content-width: 260px;
  --icon-size: 35px;
  --element-size: 80px;
  --border-color: #e0e3e7;
  --active-bg: #f0f7ff;
  --hover-bg: #f5f8fa;
  --category-bg: #f8fafc;
  --text-color: #2d3748;
  --text-light: #718096;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  display: flex;
  height: 100%;
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  background-color: #fff;
  overflow: hidden;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.modal-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 380px;
  max-width: 90%;
  overflow: hidden;
  animation: modal-appear 0.3s ease-out;
}

.modal-header {
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.modal-body {
  padding: 24px;
  text-align: center;
}

.modal-body p {
  margin-bottom: 16px;
  color: #555;
  font-size: 1rem;
}

.color-preview-modal {
  width: 60px;
  height: 60px;
  margin: 0 auto 16px;
  border-radius: 8px;
  border: 1px solid #ddd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  gap: 12px;
}

.modal-btn {
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.modal-btn-cancel {
  background-color: #f1f3f5;
  color: #495057;
}

.modal-btn-cancel:hover {
  background-color: #e9ecef;
}

.modal-btn-confirm {
  background-color: #4dabf7;
  color: white;
}

.modal-btn-confirm:hover {
  background-color: #339af0;
}

/* Animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* 1 */
@keyframes modal-appear {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.color-palette-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
}

.color-card {
  width: 53px;
  height: 53px;
  aspect-ratio: 1/1;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
  transition: all 0.2s ease;
}

.color-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.color-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 30%, rgba(0, 0, 0, 0.1) 100%);
}

.color-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.custom-color-section {
  display: flex;
  margin-top: 16px;
  padding: 12px;
  border-radius: 12px;
  justify-content: space-between;
  align-items: center;
}

.custom-color-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.custom-color-label {
  font-size: 13px;
  text-align: center;
  color: #4a5568;
  font-weight: 500;
  white-space: nowrap;
}

.color-picker-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e3e7;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.color-picker-wrapper:hover {
  border-color: #94a3b8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-picker-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.color-preview {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rainbow-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080);
}

.color-picker-icon {
  position: absolute;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.color-picker-icon svg {
  color: #4a5568;
}

.sidebar-icons {
  width: var(--sidebar-width);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  background-color: #f8fafc;
  border-right: 1px solid var(--border-color);
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
}

.sidebar-icons::-webkit-scrollbar {
  width: 4px;
}

.sidebar-icons::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-icons::-webkit-scrollbar-thumb {
  background-color: rgba(187, 202, 216, 0.6);
  border-radius: 2px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 5px;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 8px;
  transition: var(--transition);
  width: calc(100% - 16px);
  box-sizing: border-box;
  position: relative;
}

.icon-item:hover {
  background-color: var(--hover-bg);
}

.icon-item.active {
  background-color: var(--active-bg);
  box-shadow: var(--shadow-sm);
}

.icon-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 24px;
  background-color: #496782;
  border-radius: 0 3px 3px 0;
}

.icon-wrapper {
  width: var(--icon-size);
  height: var(--icon-size);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.icon-item img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
  transition: transform 0.2s ease;
}

.icon-item:hover img {
  transform: scale(1.1);
}

.icon-item span {
  font-size: 8px;
  font-weight: 500;
  text-align: center;
  color: var(--text-color);
  width: 100%;
  white-space: nowrap;
  line-height: 1.3;
  letter-spacing: 0.2px;
}

.category-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-content {
  width: var(--content-width);
  min-width: 260px;
  max-width: var(--content-width);
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d1dbe6 #fff;
  background-color: #fff;
}

.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: #fff;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background-color: #d1dbe6;
  border-radius: 3px;
}

/* Стили панели слоёв в сайдбаре */
.layers-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.layers-sidebar-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.layers-sidebar-zone-total {
  font-size: 14px;
  font-weight: 600;
  color: #496782;
}

.layers-sidebar-project-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  gap: 8px;
  white-space: nowrap;
}

.layers-sidebar-project-total .project-total-label {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  flex-shrink: 0;
}

.layers-sidebar-project-total .project-total-value {
  font-size: 14px;
  font-weight: 700;
  color: #26374e;
  min-width: 75px;
  text-align: right;
  flex-shrink: 0;
}

.calculator-open-btn-desktop {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: calc(100% - 16px);
  margin: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.calculator-open-btn-desktop:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.4);
}

.calculator-open-btn-desktop:active {
  transform: translateY(0);
}

.layers-sidebar-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.layers-sidebar-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.layers-sidebar-group {
  border-radius: 8px;
  background: #f8f9fa;
  overflow: hidden;
}

.layers-sidebar-group.is-active {
  background: #e3f2fd;
}

.layers-sidebar-group.is-dragging {
  opacity: 0.5;
}

.layers-sidebar-group.drag-over {
  border-top: 2px solid #667eea;
}

.layers-sidebar-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.layers-sidebar-group-header:hover {
  background: rgba(0, 0, 0, 0.05);
}

.group-expand-icon {
  color: #666;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.group-expand-icon.rotated {
  transform: rotate(180deg);
}

.group-icon {
  color: #667eea;
  flex-shrink: 0;
}

.layers-sidebar-group-children {
  padding-left: 20px;
  padding-bottom: 8px;
}

.layers-sidebar-item.nested {
  padding: 8px 10px;
  margin-left: 12px;
  border-left: 2px solid #e0e0e0;
}

.layers-sidebar-item.nested:hover {
  border-left-color: #667eea;
}

.layers-sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.layers-sidebar-item:hover {
  background: #f0f4f8;
}

.layers-sidebar-item.is-active {
  background: #e3f2fd;
}

.layers-sidebar-item.is-dragging {
  opacity: 0.5;
  background: #e0e0e0;
}

.layers-sidebar-item.drag-over {
  background: #d1e7ff;
  border: 2px dashed #667eea;
}

.layers-drag-handle {
  color: #aaa;
  cursor: grab;
  padding: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.layers-drag-handle:hover {
  color: #667eea;
}

.layers-sidebar-item:active .layers-drag-handle {
  cursor: grabbing;
}

.layers-sidebar-icon {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.layers-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.layers-type-icon {
  width: 22px;
  height: 22px;
  opacity: 0.6;
}

.layers-placeholder {
  width: 100%;
  height: 100%;
  background: #ddd;
}

.layers-sidebar-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.layers-sidebar-price {
  font-size: 13px;
  font-weight: 500;
  color: #496782;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

.layers-sidebar-price.locked {
  color: #999;
}

.layers-sidebar-price .lock-icon-small {
  display: inline-block;
  vertical-align: middle;
}

.favorite-star {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  padding: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.element-lock-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  padding: 4px;
  border: none;
  background: rgba(255, 200, 100, 0.95);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  color: #8B6914;
}

.element-lock-icon svg {
  width: 14px;
  height: 14px;
}

.locked-element {
  opacity: 0.7;
  position: relative;
}

.locked-element::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
}

.color-lock-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background: rgba(255, 200, 100, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8B6914;
}

.color-lock-icon svg {
  width: 10px;
  height: 10px;
}

.font-lock-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 18px;
  height: 18px;
  background: rgba(255, 200, 100, 0.95);
  border-radius: 50%;
  color: #8B6914;
}

.font-lock-icon svg {
  width: 10px;
  height: 10px;
}

.font-item.locked-element {
  opacity: 0.7;
  cursor: pointer;
}

.color-card.locked-element {
  opacity: 0.7;
  cursor: pointer;
}

.favorite-star:hover {
  transform: scale(1.1);
}

.favorite-star.is-favorite {
  opacity: 1;
}

.favorite-star svg {
  color: #999;
}

.favorite-star.is-favorite svg {
  color: #FFD700;
}

.subcategory:hover .favorite-star,
.subcategory-header:hover .favorite-star {
  opacity: 1;
}

.subcategory-header {
  position: relative;
}

.user-elements-section {
  display: contents;
}

.user-elements-header {
  display: none;
}

.user-elements-grid {
  display: contents;
}

.user-element-item {
  position: relative;
}

.delete-user-element {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  padding: 3px;
  border: none;
  background: rgba(255, 100, 100, 0.9);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.delete-user-element:hover {
  transform: scale(1.1);
  background: rgba(255, 60, 60, 1);
}

.delete-user-element svg {
  color: white;
}

.user-element-item:hover .delete-user-element,
.subcategory-header:hover .delete-user-element {
  opacity: 1;
}

/* Стили для заблокированных пользовательских элементов */
.user-element-item.locked-element {
  opacity: 0.6;
  cursor: not-allowed;
}

.user-element-item.locked-element .subcategory-header {
  cursor: not-allowed;
}

.user-element-item.locked-element .img-element {
  filter: grayscale(50%);
}

.lock-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 10;
  pointer-events: none;
}

.lock-overlay svg {
  width: 18px;
  height: 18px;
}

.upload-user-element-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 12px;
  margin-top: 12px;
  background: #f5f5f5;
  border: 1px dashed #ccc;
  border-radius: 8px;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-btn-full-width {
  margin-top: 0;
  margin-bottom: 8px;
}

.upload-user-element-btn:hover {
  background: #e8e8e8;
  border-color: #999;
  color: #333;
}

.upload-user-element-btn svg {
  color: inherit;
}

.upload-user-element-btn.locked {
  color: #999;
  border-color: #ddd;
  background: #f8f8f8;
}

.upload-user-element-btn.locked:hover {
  background: #f5f5f5;
  border-color: #ccc;
  color: #888;
}

.upload-user-element-btn .lock-icon {
  color: #999;
  margin-left: auto;
}

.upload-category-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
}

.upload-category-select:focus {
  outline: none;
  border-color: #a0b3c6;
}

.upload-modal .modal-body {
  padding: 16px;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.upload-file-area {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-file-area:hover {
  border-color: #999;
  background: #f9f9f9;
}

.upload-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.upload-file-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #999;
}

.upload-file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-file-preview img {
  max-width: 80px;
  max-height: 80px;
  object-fit: contain;
  border-radius: 4px;
}

.upload-file-preview span {
  font-size: 12px;
  color: #666;
  word-break: break-all;
  max-width: 100%;
}

.upload-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upload-field label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.upload-field input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.upload-field input:focus {
  outline: none;
  border-color: #999;
}

.search-bar {
  position: relative;
  width: 100%;
  margin-bottom: 8px;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 36px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  height: 42px;
  font-size: 14px;
  font-weight: 400;
  background-color: #fff;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: var(--transition);
  color: var(--text-color);
}

.search-input:focus {
  outline: none;
  border-color: #a0b3c6;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(73, 103, 130, 0.1);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  opacity: 0.6;
  pointer-events: none;
}

.categories {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}

.category {
  margin-bottom: 20px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background-color: var(--category-bg);
  border-radius: 10px;
  cursor: pointer;
  transition: var(--transition);
  user-select: none;
}

.category-header:hover {
  background-color: #edf2f7;
}

.category-header div {
  display: flex;
  align-items: center;
}

.category-header span {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-color);
}

.category-count {
  margin-left: 8px;
  font-size: 12px;
  color: var(--text-light);
  background-color: #e2e8f0;
  padding: 2px 6px;
  border-radius: 10px;
}

.collapse-row-icon {
  transition: var(--transition);
  display: flex;
}

.collapse-row-icon.rotated {
  transform: rotate(180deg);
}

.collapse-row-icon img {
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

.category-content {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--element-size), 1fr));
  gap: 16px;
  padding: 16px 0;
  justify-items: center;
}

.subcategory {
  width: var(--element-size);
  max-width: var(--element-size);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 4px;
}

.img-element {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  background-color: #f5f7fa;
  transition: var(--transition);
  cursor: pointer;
}

.img-element:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.font-palette {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.font-item {
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.font-item:hover {
  background: #edf2f7;
  transform: translateY(-1px);
}

/* Mobile child subcategories */
.mobile-child-subcategories-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}

.mobile-child-subcategory-item {
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
}

.mobile-child-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mobile-child-header:hover {
  background-color: #edf2f7;
}

.mobile-child-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #e2e8f0;
}

.mobile-child-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.mobile-child-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
}

.mobile-child-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.mobile-child-count {
  font-size: 12px;
  color: var(--text-light);
}

.mobile-child-elements {
  padding: 8px 12px 12px;
  background: #fff;
}

.mobile-child-elements .elements-scroll-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
}

.mobile-nested-subcategories {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-nested-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #f0f4f8;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mobile-nested-item:hover {
  background-color: #e2e8f0;
}

.mobile-nested-wrapper {
  margin-bottom: 4px;
}

.mobile-nested-elements {
  padding: 8px 0 8px 12px;
  background: #fff;
}

.mobile-nested-elements .elements-scroll-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
}

.mobile-nav-header {
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.mobile-nav-back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #e2e8f0;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: background 0.2s;
}

.mobile-nav-back-btn:hover {
  background: #cbd5e1;
}

.mobile-nav-back-btn svg {
  flex-shrink: 0;
}

.mobile-tiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  padding: 12px;
}

.mobile-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.mobile-tile:hover,
.mobile-tile:active {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: scale(0.98);
}

.mobile-tile-icon {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  background: #f1f5f9;
}

.mobile-tile-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-tile-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%);
}

.mobile-tile-name {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-tile-count {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.mobile-tile-arrow {
  font-size: 14px;
  color: #3b82f6;
  margin-top: 2px;
}

/* Адаптивные стили */
@media (max-width: 1024px) {
  .sidebar-menu {
    flex-direction: column-reverse;
    height: auto;
    border-radius: 0;
  }

  .sidebar-icons {
    flex-direction: row;
    width: 100%;
    height: auto;
    padding: 9px 0;
    overflow-x: auto;
    overflow-y: hidden;
    border-right: none;
    border-top: 1px solid var(--border-color);
  }

  .icon-item {
    margin: 0 8px;
    min-width: 58px;
    padding: 8px;
  }

  .icon-wrapper {
    width: 29px;
    height: 29px;
    margin-bottom: 4px;
  }

  .icon-item img {
    max-width: 29px;
    max-height: 29px;
  }

  .icon-item span {
    font-size: 14px;
  }

  .icon-item.active::before {
    top: auto;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 12px;
    height: 2px;
    border-radius: 2px 2px 0 0;
  }

  .sidebar-content {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 60vh;
    padding: 16px;
  }

  .search-bar {
    display: none;
  }

  .category-content {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 14px;
  }
}

@media (max-width: 480px) {
  .sidebar-icons {
    padding: 4px 0;
  }

  .icon-item {
    min-width: 51px;
    margin: 0 4px;
    padding: 4px 4px;
  }

  .icon-wrapper {
    width: 26px;
    height: 26px;
    margin-bottom: 2px;
  }

  .icon-item img {
    max-width: 26px;
    max-height: 26px;
  }

  .icon-item span {
    font-size: 10px;
    margin: 0;
    padding: 0;
  }

  .category-label {
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .category-content {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    justify-items: center;
  }

  .color-palette-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.category-transition-enter-active,
.category-transition-leave-active {
  transition: all 0.3s ease;
}

.category-transition-enter-from,
.category-transition-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 768px) {
  .sidebar-menu {
    flex-direction: column-reverse;
    height: auto;
    max-height: 55vh;
  }

  .sidebar-icons {
    flex-direction: row;
    padding: 6px 4px;
    border-bottom: 1px solid var(--border-color);
  }

  .icon-item {
    margin: 0 4px;
    min-width: 46px;
    padding: 4px 0;
  }

  .icon-wrapper {
    width: 26px;
    height: 26px;
    margin-bottom: 1px;
  }

  .icon-item img {
    max-width: 26px;
    max-height: 26px;
  }

  .icon-item span {
    font-size: 10px;
    margin: 0;
    padding: 0;
  }

  .category-label {
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .sidebar-content {
    width: 100%;
    max-height: calc(36vh - 30px);
    padding: 12px;
  }

  /* Категории - фиксированный заголовок */
  .categories {
    display: block;
    overflow: visible;
    padding: 0;
  }

  .category {
    margin-bottom: 0;
    border-bottom: 1px solid var(--border-color);
  }

  .category-header {
    position: sticky;
    top: 0;
    z-index: 5;
    background: white;
    padding: 12px;
    margin-bottom: 0;
    border-bottom: none;
  }

  /* Контент категории - горизонтальный скролл */
  .category-content {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 12px;
    gap: 12px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .category-content::-webkit-scrollbar {
    display: none;
  }

  /* Кастомные цвета - квадратные с закруглением */
  .custom-color-section {
    flex-direction: row;
    align-items: center;
    padding: 12px;
    gap: 12px;
    background: #f8f9fa;
    border-radius: 12px;
    margin: 12px;
  }

  .custom-color-picker {
    display: flex;
    align-items: center;
    gap: 12px;
    width: auto;
  }

  .color-picker-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    flex-shrink: 0;
  }

  .custom-color-label {
    white-space: nowrap;
    font-size: 14px;
  }

  /* Цветовые карточки */
  .color-palette-grid {
    display: flex;
    gap: 12px;
    padding: 0 12px;
  }

  .color-card {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    flex-shrink: 0;
  }

  /* Шрифты в одну линию */
  .font-palette {
    display: flex;
    gap: 12px;
    padding: 0 12px;
  }

  .font-item {
    min-width: 120px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Элементы в одну линию */
  .default-elements {
    display: flex;
    gap: 14px;
    padding: 0 12px;
  }

  .default-element {
    width: 80px;
    flex-shrink: 0;
  }

  .subcategory {
    min-width: 108px;
    width: 108px;
    height: auto;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .subcategory-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .subcategory-header span {
    text-align: center;
    white-space: nowrap;
    font-size: 11px;
    margin-top: 4px;
  }

  /* Кнопка избранного всегда видима на мобильных */
  .favorite-star {
    opacity: 1;
  }

  /* Пользовательские элементы - размеры для мобильных */
  .user-element-item {
    min-width: 108px;
    width: 108px;
    flex-shrink: 0;
  }

  .user-element-item .img-element {
    width: 108px;
    height: 108px;
  }

  .delete-user-element {
    opacity: 1;
  }

  .img-element {
    width: 108px;
    height: 108px;
  }
}

/* Мелкие мобильные устройства (до 480px) */
@media (max-width: 480px) {
  .sidebar-menu {
    max-height: 55vh;
  }

  .color-card {
    width: 60px;
    height: 60px;
  }

  .font-item {
    min-width: 100px;
    padding: 8px;
  }

  .default-element {
    width: 105px;
  }

  .default-elements {
    gap: 12px;
  }

  .subcategory {
    min-width: 90px;
    width: 90px;
  }

  .subcategory-header span {
    font-size: 10px;
  }

  .img-element {
    width: 90px;
    height: 90px;
  }

  .user-element-item {
    min-width: 90px;
    width: 90px;
  }

  .user-element-item .img-element {
    width: 90px;
    height: 90px;
  }

  .custom-color-picker {
    gap: 8px;
  }

  .custom-color-label {
    font-size: 13px;
  }
}

.upload-label {
  flex: 1 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 6px 12px;
  background-color: #496782;
  color: #ffffff;
  cursor: pointer;
  margin-top: 10px;
  font-size: 14px;
  border-radius: 4px;
  user-select: none;
}

/* Elements Loading Skeleton */
.elements-loading-skeleton {
  padding: 12px 0;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.skeleton-item {
  width: 100%;
  height: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 10px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@media (max-width: 768px) {
  .skeleton-grid {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }
  
  .skeleton-item {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
  }
}

/* Elements Scroll Container with Pagination */
.elements-scroll-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
  width: 100%;
  box-sizing: border-box;
  grid-column: 1 / -1;
}

.elements-scroll-container::-webkit-scrollbar {
  width: 4px;
}

.elements-scroll-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.elements-scroll-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.loading-more-indicator {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: #496782;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .elements-scroll-container {
    display: flex;
    flex-wrap: nowrap;
    grid-template-columns: none;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 8px;
  }
}

.mobile-category-sheet-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.mobile-category-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 1001;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.mobile-category-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eee;
  min-height: 56px;
}

.mobile-sheet-back-btn {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #496782;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-sheet-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
}

.mobile-sheet-close-btn {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-category-sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.mobile-subcategories-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.mobile-subcategory-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.mobile-subcategory-tile:active {
  background: #e9ecef;
  transform: scale(0.98);
}

.mobile-subcategory-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.mobile-subcategory-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.mobile-subcategory-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 200%;
  animation: shimmer 1.5s infinite;
}

.mobile-subcategory-name {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-subcategory-count {
  font-size: 10px;
  color: #888;
}

.mobile-subcategory-arrow {
  font-size: 14px;
  color: #3b82f6;
  font-weight: bold;
}

.mobile-elements-scroll-container {
  min-height: 200px;
}

.mobile-elements-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

@media (min-width: 600px) {
  .mobile-elements-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
  }
}

.mobile-load-more-indicator {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  min-height: 48px;
}

.mobile-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e0e0e0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mobile-element-tile {
  aspect-ratio: 1;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s;
}

.mobile-element-tile:active {
  transform: scale(0.95);
}

.mobile-element-tile img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.mobile-element-color {
  width: 100%;
  height: 100%;
}

.mobile-element-price {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 9px;
  padding: 2px 4px;
  border-radius: 4px;
}

.mobile-favorite-star {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #999;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  z-index: 2;
}

.mobile-element-lock {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(255, 200, 100, 0.95);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #8B6914;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  z-index: 2;
}

.mobile-element-lock svg {
  width: 14px;
  height: 14px;
}

.mobile-element-tile.locked-element {
  opacity: 0.7;
}

.mobile-delete-user-element {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #dc3545;
  z-index: 2;
}

.mobile-favorite-star:active {
  transform: scale(0.9);
}

.mobile-favorite-star.is-favorite {
  color: #FFD700;
}

.mobile-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 4px;
}

.mobile-skeleton-item {
  aspect-ratio: 1;
  background: #e8e8e8;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.mobile-skeleton-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: skeletonShimmer 1.2s ease-in-out infinite;
}

@keyframes skeletonShimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.mobile-empty-message {
  grid-column: 1 / -1;
  text-align: center;
  color: #888;
  padding: 24px;
  font-size: 14px;
}

/* Desktop categories grid */
.desktop-categories-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 8px 0;
}

.desktop-category-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.desktop-category-tile:hover {
  background: #e9ecef;
}

.desktop-category-tile:active {
  transform: scale(0.98);
}

.desktop-category-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.desktop-category-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.desktop-category-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 200%;
  animation: shimmer 1.5s infinite;
}

.desktop-category-name {
  font-size: 12px;
  font-weight: 500;
  color: #496782;
  text-align: center;
  line-height: 1.2;
}

.desktop-category-count {
  font-size: 10px;
  color: #888;
}

/* Desktop elements header */
.desktop-elements-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.desktop-back-btn {
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #496782;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.desktop-back-btn:hover {
  background: #f0f0f0;
}

.desktop-elements-title {
  font-size: 14px;
  font-weight: 600;
  color: #496782;
}

/* Desktop elements grid - 3 columns */
.desktop-elements-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
  padding: 4px 0;
}

.desktop-skeleton-grid {
  grid-template-columns: repeat(3, 1fr);
}

.desktop-element-tile {
  display: flex;
  flex-direction: column;
  position: relative;
}

.desktop-element-content {
  aspect-ratio: 1;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
}

.desktop-element-content:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.desktop-element-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.desktop-element-tile .element-price {
  font-size: 10px;
  color: #888;
  text-align: center;
  margin-top: 2px;
}

.desktop-element-tile .favorite-star {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.desktop-element-content:hover .favorite-star {
  opacity: 1;
}

.desktop-element-tile .favorite-star.is-favorite {
  opacity: 1;
}

.desktop-element-tile .delete-user-element {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  color: #dc3545;
}

.desktop-element-content:hover .delete-user-element {
  opacity: 1;
}

.desktop-element-tile .lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
}
</style>
